import { useState, useMemo, useEffect, useRef, Component, createContext, useContext } from "react";
import { loadAllData, saveData, loginUser, createUser, listUsers, deleteUser, updateUserPin, hasUsers, setCurrentUserId, getCurrentUserId } from "./supabaseClient";

var APP_VERSION = "1.16.3";

// BudgetContext bundles all app state and updaters in one place,
// avoiding deep prop chains. Tabs read what they need via useBudget().
var BudgetContext = createContext(null);
function useBudget() {
  var ctx = useContext(BudgetContext);
  if (!ctx) throw new Error("useBudget must be used within BudgetContext.Provider");
  return ctx;
}

var THEMES = {
  dark: {
    bg: "linear-gradient(170deg, #060a14, #0e1525, #080d18)",
    cardBg: "rgba(15,22,38,0.6)",
    cardBd: "rgba(100,160,255,0.07)",
    rowEven: "rgba(20,30,50,0.5)",
    rowOdd: "rgba(28,40,65,0.5)",
    rowBd: "rgba(100,160,255,0.04)",
    secBd: "rgba(100,160,255,0.12)",
    textPrimary: "#c0d8e8",
    textSecondary: "#8caac8",
    textMuted: "#4e6180",
    textDim: "#3a506e",
    accent: "#4a8fe7",
    green: "#5cb88a",
    red: "#c45c5c",
    orange: "#c49060",
    yellow: "#e6c850",
    purple: "#a98be0",
    inputBg: "rgba(8,12,24,0.9)",
    inputBd: "rgba(100,160,255,0.12)",
    navBg: "#0a0f1a",
    navBd: "rgba(100,160,255,0.08)",
    btnBg: "#1d4e89",
    btnText: "#c0d8f0",
    lockBg: "rgba(15,22,38,0.6)",
    pinKeyBg: "rgba(14,21,37,0.8)",
    pinKeyBd: "#162035",
    pinKeyText: "#8caac8",
    pinDotActive: "#4a8fe7",
    pinDotInactive: "#1e2d44",
    headerColor: "#7a9ab8",
    nameColor: "#7a9ab8",
    barTrack: "#162035",
    dayCellBg: "rgba(15,22,38,0.5)",
    dayCellBd: "rgba(100,160,255,0.04)",
  },
  light: {
    bg: "linear-gradient(170deg, #eef1f5, #e4e8ee, #eef1f5)",
    cardBg: "rgba(255,255,255,0.95)",
    cardBd: "rgba(0,30,80,0.08)",
    rowEven: "rgba(255,255,255,0.7)",
    rowOdd: "rgba(240,244,250,0.8)",
    rowBd: "rgba(0,30,80,0.06)",
    secBd: "rgba(0,30,80,0.12)",
    textPrimary: "#1e2d3d",
    textSecondary: "#2a4055",
    textMuted: "#5a7085",
    textDim: "#7a8ea0",
    accent: "#2563eb",
    green: "#16803c",
    red: "#c42020",
    orange: "#b86e10",
    yellow: "#9a7008",
    purple: "#6b3fa0",
    inputBg: "#ffffff",
    inputBd: "rgba(0,30,80,0.15)",
    navBg: "#ffffff",
    navBd: "rgba(0,30,80,0.08)",
    btnBg: "#2563eb",
    btnText: "#ffffff",
    lockBg: "rgba(255,255,255,0.8)",
    pinKeyBg: "rgba(240,242,248,0.95)",
    pinKeyBd: "rgba(0,30,80,0.1)",
    pinKeyText: "#2a4055",
    pinDotActive: "#2563eb",
    pinDotInactive: "rgba(0,30,80,0.12)",
    headerColor: "#2a4055",
    nameColor: "#2a4055",
    barTrack: "rgba(0,30,80,0.08)",
    dayCellBg: "rgba(255,255,255,0.55)",
    dayCellBd: "rgba(0,30,80,0.06)",
  }
};

// Error boundary — catches render-time exceptions in any child tab/component
// and shows a friendly fallback instead of a blank white screen. Each tab is
// wrapped individually so a crash in one tab doesn't take down the whole app.
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error: error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);
  }
  reset = () => { this.setState({ error: null }); };
  render() {
    if (this.state.error) {
      var label = this.props.label || "this section";
      return (
        <div style={{ padding: 20, margin: 12, borderRadius: 12, border: "1px solid " + T().red, background: T().cardBg, color: T().textPrimary, fontFamily: "system-ui, sans-serif" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T().red, marginBottom: 6 }}>Something went wrong in {label}</div>
          <div style={{ fontSize: 12, color: T().textMuted, marginBottom: 10 }}>{String(this.state.error && this.state.error.message ? this.state.error.message : this.state.error)}</div>
          <button onClick={this.reset} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid " + T().accent, background: "transparent", color: T().accent, cursor: "pointer", fontSize: 12, fontFamily: "system-ui", fontWeight: 600 }}>Try again</button>
          <div style={{ fontSize: 10, color: T().textDim, marginTop: 10 }}>Your data is safe — this only affects what's currently displayed.</div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Empty initial state. The app loads real data from Supabase after login.
// These skeletons are used only at boot/logout and as the starting point
// for genuinely new users (no rows in DB yet). They MUST NOT contain user
// data — past versions had hardcoded bills/cards which would silently
// overwrite real data if a load failed and the user then made an edit.
const INIT_BILLS = [];

const INIT_INCOME = {
  salary: { current: 0, future1: 0, future2: 0 },
  va: { current: 0, future1: 0, future2: 0 },
  bonus: { current: 0, future1: 0, future2: 0 },
};

const INIT_PAYROLL = {
  frequency: "biweekly",
  firstPayDate: "",
};

const INIT_CARDS = [];

var DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function fm(n) {
  if (!n && n !== 0) return "$0";
  var neg = n < 0;
  var s = Math.abs(Math.round(n)).toLocaleString("en-US");
  return neg ? "-$" + s : "$" + s;
}

function fp(n) {
  return isNaN(n) ? "0%" : (n * 100).toFixed(1) + "%";
}

function fd(d) {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// Local-timezone YYYY-MM-DD string for a Date. Used for comparing calendar
// grid dates against user-entered date strings (bill end dates, etc.) without
// the UTC shift that toISOString() introduces for evening hours in negative
// UTC offsets.
function localISO(d) {
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
}

function estPayment(balance, apr, minPct, isZero) {
  if (balance <= 0) return 0;
  var pctAmt = balance * (minPct / 100);
  return Math.round(Math.max(pctAmt, Math.min(25, balance)));
}

function ordinal(n) {
  if (n === 1 || n === 21 || n === 31) return n + "st";
  if (n === 2 || n === 22) return n + "nd";
  if (n === 3 || n === 23) return n + "rd";
  return n + "th";
}

// Resolves the effective VA amount for a given Date. vaChanges is an array
// of { id, effectiveDate: "YYYY-MM-DD", newAmount: number }. The function
// returns the newAmount of the most recent change whose effectiveDate is on
// or before the given date; if no change applies yet, returns the baseline
// income.va.current. Changes with invalid dates or non-numeric amounts are
// skipped defensively.
function effectiveVA(date, baselineVA, vaChanges) {
  return effectiveAmount(date, baselineVA, vaChanges);
}

// Same algorithm as effectiveVA, applied to monthly salary scheduled changes.
// The caller is still responsible for converting monthly salary to per-paycheck
// amount based on payroll.frequency.
function effectiveSalary(date, baselineSalary, salaryChanges) {
  return effectiveAmount(date, baselineSalary, salaryChanges);
}

// Generic "what amount applies on this date" lookup used by both VA and salary
// scheduled changes. changes is an array of { effectiveDate, newAmount }.
// Returns the newAmount of the latest change with effectiveDate <= date,
// falling back to baseline if none apply yet.
function effectiveAmount(date, baseline, changes) {
  if (!changes || changes.length === 0) return baseline;
  var d = date instanceof Date ? date : new Date(date + "T12:00:00");
  if (isNaN(d.getTime())) return baseline;
  var applicable = null;
  var applicableTime = -Infinity;
  for (var i = 0; i < changes.length; i++) {
    var c = changes[i];
    if (!c || !c.effectiveDate || typeof c.newAmount !== "number") continue;
    var ed = new Date(c.effectiveDate + "T12:00:00");
    if (isNaN(ed.getTime())) continue;
    var edTime = ed.getTime();
    if (ed <= d && edTime > applicableTime) {
      applicable = c;
      applicableTime = edTime;
    }
  }
  return applicable ? applicable.newAmount : baseline;
}

// Project card balances forward to a given date
function projectCardBalances(cards, targetDate, oneTimeTxs) {
  var now = new Date();
  now.setHours(0, 0, 0, 0);
  var target = new Date(targetDate + "T12:00:00");
  if (target <= now) return null; // no projection needed

  // Group card-linked one-time payments by card id and by year-month bucket.
  // Per option B: c.balance is the "as of right now" baseline. Past-dated txs are
  // pure historical records and were already factored into c.balance by the user.
  // Today and future txs project forward from the baseline.
  var scheduledByCard = {};
  if (oneTimeTxs && oneTimeTxs.length > 0) {
    oneTimeTxs.forEach(function (tx) {
      if (!tx.cardId) return;
      var txDate = new Date(tx.date + "T12:00:00");
      if (txDate < now || txDate > target) return; // include today and future, up to target
      var ym = txDate.getFullYear() * 12 + txDate.getMonth();
      var key = String(tx.cardId);
      if (!scheduledByCard[key]) scheduledByCard[key] = {};
      scheduledByCard[key][ym] = (scheduledByCard[key][ym] || 0) + Math.abs(tx.amount);
    });
  }

  var projected = {};
  var nowYM = now.getFullYear() * 12 + now.getMonth();
  var targetYM = target.getFullYear() * 12 + target.getMonth();
  // Walk from current month (m=0) through target month (m=monthsSpan) inclusive.
  var monthsSpan = targetYM - nowYM;

  cards.forEach(function (c) {
    var sched = scheduledByCard[String(c.id)] || {};
    var hasScheduled = Object.keys(sched).length > 0;

    // Used-mode (or zero-balance) cards with no scheduled paydown stay at current balance.
    if ((c.mode !== "paydown" || c.balance <= 0) && !hasScheduled) {
      projected[c.id] = c.balance;
      return;
    }

    var bal = c.balance;
    var isPaydown = c.mode === "paydown";

    // Walk month by month. Index 0 is the current calendar month — c.balance is
    // already current so we skip interest and min payment for it (the user's
    // current-month statement already reflects them), but we DO apply any
    // scheduled tx landing in the current month since those are forward-looking
    // events the user has scheduled.
    for (var m = 0; m <= monthsSpan; m++) {
      if (bal <= 0) break;
      var ym = nowYM + m;

      if (m > 0) {
        // For each subsequent month: apply interest, scheduled tx, then min payment
        if (c.apr > 0 && !c.zero) {
          bal += bal * (c.apr / 100 / 12);
        }
        if (sched[ym]) {
          bal = Math.max(0, bal - sched[ym]);
          if (bal <= 0) break;
        }
        if (isPaydown) {
          var pmt = estPayment(bal, c.apr, c.minPct, c.zero);
          pmt = Math.min(pmt, bal);
          bal = Math.max(0, bal - pmt);
        }
      } else {
        // Current month: only apply scheduled tx (no interest, no min payment —
        // those are already baked into the user-maintained current balance).
        if (sched[ym]) {
          bal = Math.max(0, bal - sched[ym]);
        }
      }
    }
    projected[c.id] = Math.round(bal);
  });
  return projected;
}

function getPayDates(payroll, startDate, endDate) {
  var dates = [];
  if (!payroll || !payroll.firstPayDate) return dates;
  var freq = payroll.frequency;
  var first = new Date(payroll.firstPayDate + "T12:00:00");
  if (isNaN(first.getTime())) return dates;

  if (freq === "monthly") {
    var dom = first.getDate();
    var d = new Date(startDate);
    d.setDate(dom);
    if (d < startDate) d.setMonth(d.getMonth() + 1);
    while (d <= endDate) {
      dates.push(new Date(d));
      d.setMonth(d.getMonth() + 1);
    }
  } else if (freq === "bimonthly") {
    var d1 = first.getDate();
    var d2 = d1 + 15;
    if (d2 > 28) d2 = d2 - 28;
    var cur = new Date(startDate);
    cur.setDate(1);
    while (cur <= endDate) {
      var m = cur.getMonth();
      var y = cur.getFullYear();
      var pay1 = new Date(y, m, d1, 12);
      var pay2 = new Date(y, m, Math.min(d2, 28), 12);
      if (pay1 >= startDate && pay1 <= endDate) dates.push(pay1);
      if (pay2 >= startDate && pay2 <= endDate) dates.push(pay2);
      cur.setMonth(cur.getMonth() + 1);
    }
  } else if (freq === "weekly") {
    var current = new Date(first);
    while (current < startDate) current.setDate(current.getDate() + 7);
    while (current > startDate) current.setDate(current.getDate() - 7);
    if (current < startDate) current.setDate(current.getDate() + 7);
    while (current <= endDate) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 7);
    }
  } else {
    var current2 = new Date(first);
    while (current2 < startDate) current2.setDate(current2.getDate() + 14);
    while (current2 > startDate) current2.setDate(current2.getDate() - 14);
    if (current2 < startDate) current2.setDate(current2.getDate() + 14);
    while (current2 <= endDate) {
      dates.push(new Date(current2));
      current2.setDate(current2.getDate() + 14);
    }
  }
  return dates;
}

/* LOGIN SCREEN */
function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState("");
  const [pin, setPin] = useState("");
  const [phase, setPhase] = useState("username"); // username | pin | setup
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [pinErr, setPinErr] = useState(false);
  const [checking, setChecking] = useState(false);
  const [setupUser, setSetupUser] = useState("");
  const [setupPin, setSetupPin] = useState("");
  const [setupConfirm, setSetupConfirm] = useState("");

  // Check if any users exist — if not, show first-time setup
  useEffect(function () {
    hasUsers().then(function (exists) {
      if (!exists) setPhase("setup");
      setLoading(false);
    });
  }, []);

  function submitUsername() {
    if (!username.trim()) return;
    setPhase("pin");
    setErr("");
  }

  function tap(d) {
    if (pin.length >= 6 || checking) return;
    var next = pin + d;
    setPin(next);
    setPinErr(false);
    if (next.length >= 4) {
      setChecking(true);
      loginUser(username, next).then(function (result) {
        setChecking(false);
        if (result.success) {
          onLogin(result.user);
        } else if (next.length >= 6) {
          setPinErr(true);
          setErr("Invalid PIN");
          setTimeout(function () { setPin(""); setPinErr(false); setErr(""); }, 800);
        }
      });
    }
  }

  function handleSetup() {
    if (!setupUser.trim()) { setErr("Enter a username"); return; }
    if (setupPin.length < 4) { setErr("PIN must be at least 4 digits"); return; }
    if (setupPin !== setupConfirm) { setErr("PINs don't match"); return; }
    setLoading(true);
    createUser(setupUser, setupPin, true).then(function (result) {
      setLoading(false);
      if (result.success) {
        loginUser(setupUser, setupPin).then(function (lr) {
          if (lr.success) onLogin(lr.user);
        });
      } else {
        setErr(result.error);
      }
    });
  }

  if (loading) {
    return (
      <div style={{ height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "linear-gradient(170deg, #060a14, #0e1525, #080d18)", fontFamily: "system-ui, sans-serif", color: T().textPrimary }}>
        <div style={{ fontSize: 22, fontWeight: 600, color: T().textSecondary, marginBottom: 8 }}>Vault Budget</div>
        <div style={{ fontSize: 12, color: T().textDim }}>Loading...</div>
      </div>
    );
  }

  var inputStyle = { width: "100%", padding: "12px 14px", background: "rgba(14,21,37,0.8)", border: "1px solid rgba(100,160,255,0.15)", borderRadius: 10, color: T().textPrimary, fontSize: 15, fontFamily: "monospace", outline: "none", textAlign: "center", boxSizing: "border-box" };
  var btnStyle = { padding: "10px 32px", borderRadius: 8, border: "none", background: T().btnBg, color: T().btnText, cursor: "pointer", fontSize: 14, fontFamily: "system-ui", fontWeight: 600, width: "100%" };

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "linear-gradient(170deg, #060a14, #0e1525, #080d18)", fontFamily: "system-ui, sans-serif", color: T().textPrimary }}>
      <div style={{ fontSize: 22, fontWeight: 600, color: T().textSecondary, marginBottom: 2 }}>Vault Budget</div>
      <div style={{ fontSize: 10, color: T().textDim, marginBottom: 4 }}>v{APP_VERSION}</div>

      {phase === "setup" && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 20, width: 280 }}>
          <div style={{ fontSize: 13, color: T().green, marginBottom: 4, fontWeight: 600 }}>First-Time Setup</div>
          <div style={{ fontSize: 11, color: T().textDim, marginBottom: 16, textAlign: "center" }}>Create your admin account</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%" }}>
            <input value={setupUser} onChange={function (e) { setSetupUser(e.target.value); }} placeholder="Choose a username" style={inputStyle} />
            <input type="password" value={setupPin} onChange={function (e) { setSetupPin(e.target.value.replace(/[^0-9]/g, "").slice(0, 6)); }} placeholder="Choose PIN (4-6 digits)" style={{ ...inputStyle, letterSpacing: 6 }} />
            <input type="password" value={setupConfirm} onChange={function (e) { setSetupConfirm(e.target.value.replace(/[^0-9]/g, "").slice(0, 6)); }} placeholder="Confirm PIN" onKeyDown={function (e) { if (e.key === "Enter") handleSetup(); }} style={{ ...inputStyle, letterSpacing: 6 }} />
            <button onClick={handleSetup} style={btnStyle}>Create Account</button>
          </div>
          {err && <div style={{ fontSize: 11, color: T().red, marginTop: 10 }}>{err}</div>}
        </div>
      )}

      {phase === "username" && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 20, width: 260 }}>
          <div style={{ fontSize: 12, color: T().textDim, marginBottom: 16 }}>Enter username</div>
          <input value={username} onChange={function (e) { setUsername(e.target.value); }} onKeyDown={function (e) { if (e.key === "Enter") submitUsername(); }} placeholder="username" autoFocus style={{ ...inputStyle, border: "1px solid " + (err ? T().red : "rgba(100,160,255,0.15)") }} />
          <button onClick={submitUsername} style={{ ...btnStyle, marginTop: 12, width: "auto", padding: "10px 32px" }}>Continue</button>
          {err && <div style={{ fontSize: 11, color: T().red, marginTop: 10 }}>{err}</div>}
        </div>
      )}

      {phase === "pin" && (
        <div
          tabIndex={0}
          ref={function (el) { if (el) el.focus(); }}
          onKeyDown={function (e) {
            if (e.key >= "0" && e.key <= "9") { tap(e.key); e.preventDefault(); }
            else if (e.key === "Backspace") { setPin(pin.slice(0, -1)); e.preventDefault(); }
          }}
          style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 12, outline: "none" }}
        >
          <div style={{ fontSize: 12, color: T().textDim, marginBottom: 6 }}>{username}</div>
          <div style={{ fontSize: 12, color: T().textDim, marginBottom: 28 }}>Enter PIN</div>
          <div style={{ display: "flex", gap: 14, marginBottom: 32 }}>
            {[0, 1, 2, 3, 4, 5].map(function (i) {
              return <div key={i} style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid " + (pinErr ? T().red : pin.length > i ? T().accent : T().pinDotInactive), background: pin.length > i ? (pinErr ? T().red : T().accent) : "transparent", transition: "all 0.2s" }} />;
            })}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 64px)", gap: 10 }}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, "del"].map(function (k, i) {
              return (
                <button key={i} onClick={function () { if (k === "del") setPin(pin.slice(0, -1)); else if (k !== null) tap(String(k)); }} style={{ width: 64, height: 64, borderRadius: "50%", border: k === null ? "none" : "1px solid " + T().pinKeyBd, background: k === null || k === "del" ? "transparent" : T().pinKeyBg, color: k === "del" ? T().textDim : T().textSecondary, fontSize: k === "del" ? 12 : 20, fontWeight: 300, fontFamily: "system-ui", cursor: k === null ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: k === null ? "none" : "auto" }}>
                  {k === "del" ? "DEL" : k}
                </button>
              );
            })}
          </div>
          {err && <div style={{ fontSize: 11, color: T().red, marginTop: 12 }}>{err}</div>}
          <button onClick={function () { setPhase("username"); setPin(""); setErr(""); }} style={{ background: "none", border: "none", color: T().textDim, cursor: "pointer", fontSize: 11, marginTop: 16, fontFamily: "system-ui" }}>Back</button>
        </div>
      )}
    </div>
  );
}

/* Numeric input that allows decimals */
function NumInput({ value, onChange, style }) {
  const [draft, setDraft] = useState(String(value));
  const [focused, setFocused] = useState(false);

  var display = focused ? draft : String(value);

  return (
    <input
      value={display}
      onChange={function (e) { setDraft(e.target.value); }}
      onFocus={function () { setDraft(String(value)); setFocused(true); }}
      onBlur={function () { onChange(parseFloat(draft) || 0); setFocused(false); }}
      onKeyDown={function (e) { if (e.key === "Enter") { onChange(parseFloat(draft) || 0); setFocused(false); e.target.blur(); } }}
      style={style}
    />
  );
}

/* Text input that commits on blur */
function TextInput({ value, onChange, style }) {
  const [draft, setDraft] = useState(String(value));
  const [focused, setFocused] = useState(false);
  var display = focused ? draft : String(value);
  return (
    <input
      value={display}
      onChange={function (e) { setDraft(e.target.value); }}
      onFocus={function () { setDraft(String(value)); setFocused(true); }}
      onBlur={function () { onChange(draft); setFocused(false); }}
      onKeyDown={function (e) { if (e.key === "Enter") { onChange(draft); setFocused(false); e.target.blur(); } }}
      style={style}
    />
  );
}

/* Editable cell */
function EC({ value, onChange, color }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  if (editing) {
    return <input autoFocus value={draft} onChange={function (e) { setDraft(e.target.value); }} onBlur={function () { onChange(parseFloat(draft) || 0); setEditing(false); }} onKeyDown={function (e) { if (e.key === "Enter") { onChange(parseFloat(draft) || 0); setEditing(false); } }} style={{ width: 72, padding: "5px 6px", background: T().inputBg, border: "1px solid rgba(74,143,231,0.3)", borderRadius: 5, color: T().textPrimary, fontSize: 13, fontFamily: "monospace", outline: "none", textAlign: "right", boxSizing: "border-box" }} />;
  }
  return <span onClick={function () { setDraft(String(value)); setEditing(true); }} style={{ cursor: "pointer", borderBottom: "1px dashed rgba(100,160,255,0.2)", paddingBottom: 1, color: color || T().textPrimary }}>{fm(value)}</span>;
}

// Active theme ref — set by main component, read by all children
var _t = THEMES.dark;
function T() { return _t; }

// Utilization color tiers: purple=0%, green<=28%, yellow 29-49%, orange 50-89%, red>=90%
// Takes utilization as decimal (0.0-1.0+), returns theme color
function utilColor(u) {
  if (u <= 0) return T().purple;
  if (u >= 0.9) return T().red;
  if (u >= 0.5) return T().orange;
  if (u >= 0.29) return T().yellow;
  return T().green;
}

// Translucent row-background tint for the same utilization tiers — used to highlight
// card rows in Debt and Settings tabs. Alpha is low so text and inputs remain readable
// on top of both light and dark themes.
function utilRowBg(u) {
  if (u <= 0) return "rgba(169,139,224,0.18)";   // purple
  if (u >= 0.9) return "rgba(196,92,92,0.20)";   // red
  if (u >= 0.5) return "rgba(196,144,96,0.20)";  // orange
  if (u >= 0.29) return "rgba(230,200,80,0.18)"; // yellow
  return "rgba(92,184,138,0.18)";                // green
}
function setActiveTheme(theme) {
  _t = theme;
  hs = { fontSize: 9, color: theme.textMuted, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700, padding: "7px 4px", textAlign: "right" };
  cs = { padding: "6px 4px", fontSize: 13, textAlign: "right", fontFamily: "monospace", color: theme.textPrimary };
  ns = { padding: "6px 4px", fontSize: 13, color: theme.nameColor, textAlign: "left", fontWeight: 500 };
  cardBg = theme.cardBg;
  cardBd = "1px solid " + theme.cardBd;
  rowBd = "1px solid " + theme.rowBd;
  secBd = "2px solid " + theme.secBd;
  rowEven = theme.rowEven;
  rowOdd = theme.rowOdd;
}

// Shared styles — initialized to dark theme, updated by setActiveTheme
var hs = { fontSize: 9, color: "#4e6180", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700, padding: "7px 4px", textAlign: "right" };
var cs = { padding: "6px 4px", fontSize: 13, textAlign: "right", fontFamily: "monospace", color: "#c0d8e8" };
var ns = { padding: "6px 4px", fontSize: 13, color: "#7a9ab8", textAlign: "left", fontWeight: 500 };
var cardBg = "rgba(15,22,38,0.6)";
var cardBd = "1px solid rgba(100,160,255,0.07)";
var rowBd = "1px solid rgba(100,160,255,0.04)";
var secBd = "2px solid rgba(100,160,255,0.12)";
var rowEven = "rgba(20,30,50,0.5)";
var rowOdd = "rgba(28,40,65,0.5)";

/* BILLS TAB */
function BillsTab() {
  var ctx = useBudget();
  var bills = ctx.bills, setBills = ctx.setBills;
  var income = ctx.income, setIncome = ctx.setIncome;
  var cards = ctx.cards, setCards = ctx.setCards;
  var payroll = ctx.payroll;
  var oneTimeTxs = ctx.oneTimeTxs;
  var projDate = ctx.projDate, setProjDate = ctx.setProjDate;
  const [pieScenario, setPieScenario] = useState("current");
  var projectedBals = projDate ? projectCardBalances(cards, projDate, oneTimeTxs) : null;
  var displayCards = projectedBals ? cards.map(function (c) {
    var projBal = projectedBals[c.id] !== undefined ? projectedBals[c.id] : c.balance;
    return { ...c, balance: projBal };
  }) : cards.slice();
  displayCards.sort(function (a, b) { return a.dueDay - b.dueDay; });

  var ccPay = displayCards.reduce(function (s, c) { return s + estPayment(c.balance, c.apr, c.minPct, c.zero); }, 0);
  // Bills shown on this tab: any bill not manually hidden via the per-bill
  // "Hide" toggle in Settings → Bill Settings. Hidden bills are excluded from
  // the list, totals, leftover, and pie chart — a manual declutter for
  // planning across Current/Future 1/Future 2. The Calendar is NOT affected
  // by the Hide flag; its per-day start/end date gating always applies.
  var activeBills = bills.filter(function (b) { return !b.hidden; });
  var hiddenBillCount = bills.length - activeBills.length;
  var totB = { c: 0, f1: 0, f2: 0 };
  activeBills.forEach(function (b) { totB.c += b.current; totB.f1 += b.future1; totB.f2 += b.future2; });
  var totIn = { c: income.salary.current + income.va.current, f1: income.salary.future1 + income.va.future1, f2: income.salary.future2 + income.va.future2 };
  var left = { c: totIn.c - totB.c - ccPay, f1: totIn.f1 - totB.f1 - ccPay, f2: totIn.f2 - totB.f2 - ccPay };
  var extraPay = payroll.frequency === "biweekly" || payroll.frequency === "weekly";
  var ann = {
    c: left.c * 12 + (extraPay ? income.salary.current : 0) + income.bonus.current,
    f1: left.f1 * 12 + (extraPay ? income.salary.future1 : 0) + income.bonus.future1,
    f2: left.f2 * 12 + (extraPay ? income.salary.future2 : 0) + income.bonus.future2
  };
  var tBal = displayCards.reduce(function (s, c) { return s + c.balance; }, 0);
  var tLim = displayCards.reduce(function (s, c) { return s + c.limit; }, 0);
  var tInt = displayCards.reduce(function (s, c) { return s + (c.apr > 0 && !c.zero ? c.balance * (c.apr / 100 / 12) : 0); }, 0);

  return (
    <div style={{ padding: "0 12px 130px" }}>
      {/* Projection date picker */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: T().headerColor, fontWeight: 500, whiteSpace: "nowrap" }}>Viewing:</div>
        <button onClick={function () { setProjDate(""); }} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid " + (!projDate ? "rgba(74,143,231,0.35)" : "rgba(100,160,255,0.08)"), background: !projDate ? "rgba(74,143,231,0.12)" : "transparent", color: !projDate ? T().accent : T().textDim, cursor: "pointer", fontSize: 12, fontFamily: "system-ui", fontWeight: !projDate ? 600 : 400 }}>Today</button>
        <input type="date" value={projDate} onChange={function (e) { setProjDate(e.target.value); }} style={{ padding: "6px 10px", background: T().inputBg, border: "1px solid " + T().inputBd, borderRadius: 6, color: T().textPrimary, fontSize: 12, fontFamily: "monospace", outline: "none", boxSizing: "border-box" }} />
        {projDate && <div style={{ fontSize: 10, color: T().orange }}>Showing projected balances for paydown cards</div>}
      </div>

      {/* Summary */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
        {[{ l: "Current", v: left.c }, { l: "Future 1", v: left.f1 }, { l: "Future 2", v: left.f2 }].map(function (s, i) {
          return (
            <div key={i} style={{ background: cardBg, borderRadius: 12, border: cardBd, padding: "12px 8px", textAlign: "center" }}>
              <div style={{ fontSize: 10, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>{s.l}</div>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "monospace", marginTop: 4, color: s.v >= 0 ? T().green : T().red }}>{fm(s.v)}</div>
              <div style={{ fontSize: 9, color: T().textDim, marginTop: 2 }}>monthly leftover</div>
            </div>
          );
        })}
      </div>

      {/* Bills Table */}
      <div style={{ background: cardBg, borderRadius: 12, border: cardBd, overflow: "hidden", marginBottom: 12 }}>
        <div style={{ padding: "12px 14px 8px" }}>
          <span style={{ fontSize: 11, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>Bills and Income</span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 380 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(100,160,255,0.08)" }}>
                <th style={{ ...hs, textAlign: "left", paddingLeft: 14 }}>Expense</th>
                <th style={hs}>Current</th>
                <th style={hs}>Future 1</th>
                <th style={{ ...hs, paddingRight: 14 }}>Future 2</th>
              </tr>
            </thead>
            <tbody>
              {activeBills.slice().sort(function (a, b) { return a.dueDay - b.dueDay; }).map(function (b, idx) {
                return (
                  <tr key={b.id} style={{ borderBottom: rowBd, background: idx % 2 === 0 ? rowEven : rowOdd }}>
                    <td style={{ ...ns, paddingLeft: 14 }}>{b.name}</td>
                    <td style={cs}><EC value={b.current} onChange={function (v) { setBills(function (prev) { return prev.map(function (x) { return x.id === b.id ? { ...x, current: v } : x; }); }); }} /></td>
                    <td style={cs}><EC value={b.future1} onChange={function (v) { setBills(function (prev) { return prev.map(function (x) { return x.id === b.id ? { ...x, future1: v } : x; }); }); }} /></td>
                    <td style={{ ...cs, paddingRight: 14 }}><EC value={b.future2} onChange={function (v) { setBills(function (prev) { return prev.map(function (x) { return x.id === b.id ? { ...x, future2: v } : x; }); }); }} /></td>
                  </tr>
                );
              })}
              {hiddenBillCount > 0 && (
                <tr>
                  <td colSpan={4} style={{ padding: "6px 14px", fontSize: 10, color: T().textDim, fontStyle: "italic" }}>
                    {hiddenBillCount} bill{hiddenBillCount > 1 ? "s" : ""} hidden — unhide in Settings → Bill Settings.
                  </td>
                </tr>
              )}
              <tr style={{ borderBottom: rowBd }}>
                <td style={{ ...ns, paddingLeft: 14, color: T().orange }}>CC Payments</td>
                <td style={{ ...cs, color: T().orange }}>{fm(ccPay)}</td>
                <td style={{ ...cs, color: T().orange }}>{fm(ccPay)}</td>
                <td style={{ ...cs, color: T().orange, paddingRight: 14 }}>{fm(ccPay)}</td>
              </tr>
              <tr style={{ borderTop: secBd }}>
                <td style={{ ...ns, paddingLeft: 14, fontWeight: 700, color: T().textSecondary }}>Total Out</td>
                <td style={{ ...cs, fontWeight: 700, color: T().red }}>{fm(totB.c + ccPay)}</td>
                <td style={{ ...cs, fontWeight: 700, color: T().red }}>{fm(totB.f1 + ccPay)}</td>
                <td style={{ ...cs, fontWeight: 700, color: T().red, paddingRight: 14 }}>{fm(totB.f2 + ccPay)}</td>
              </tr>
              <tr><td colSpan={4} style={{ padding: "10px 14px 4px", fontSize: 10, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700, background: "rgba(92,184,138,0.04)" }}>Income</td></tr>
              <tr style={{ borderBottom: rowBd, background: "rgba(92,184,138,0.03)" }}>
                <td style={{ ...ns, paddingLeft: 14, color: T().green }}>Salary/Month</td>
                <td style={cs}><EC value={income.salary.current} onChange={function (v) { setIncome(function (prev) { return { ...prev, salary: { ...prev.salary, current: v } }; }); }} color={T().green} /></td>
                <td style={cs}><EC value={income.salary.future1} onChange={function (v) { setIncome(function (prev) { return { ...prev, salary: { ...prev.salary, future1: v } }; }); }} color={T().green} /></td>
                <td style={{ ...cs, paddingRight: 14 }}><EC value={income.salary.future2} onChange={function (v) { setIncome(function (prev) { return { ...prev, salary: { ...prev.salary, future2: v } }; }); }} color={T().green} /></td>
              </tr>
              <tr style={{ borderBottom: rowBd, background: "rgba(92,184,138,0.03)" }}>
                <td style={{ ...ns, paddingLeft: 14, color: T().green }}>VA</td>
                <td style={cs}><EC value={income.va.current} onChange={function (v) { setIncome(function (prev) { return { ...prev, va: { ...prev.va, current: v } }; }); }} color={T().green} /></td>
                <td style={cs}><EC value={income.va.future1} onChange={function (v) { setIncome(function (prev) { return { ...prev, va: { ...prev.va, future1: v } }; }); }} color={T().green} /></td>
                <td style={{ ...cs, paddingRight: 14 }}><EC value={income.va.future2} onChange={function (v) { setIncome(function (prev) { return { ...prev, va: { ...prev.va, future2: v } }; }); }} color={T().green} /></td>
              </tr>
              <tr style={{ borderTop: secBd, background: "rgba(92,184,138,0.03)" }}>
                <td style={{ ...ns, paddingLeft: 14, fontWeight: 700, color: T().green }}>Total In</td>
                <td style={{ ...cs, fontWeight: 700, color: T().green }}>{fm(totIn.c)}</td>
                <td style={{ ...cs, fontWeight: 700, color: T().green }}>{fm(totIn.f1)}</td>
                <td style={{ ...cs, fontWeight: 700, color: T().green, paddingRight: 14 }}>{fm(totIn.f2)}</td>
              </tr>
              <tr style={{ background: "rgba(100,160,255,0.06)" }}>
                <td style={{ ...ns, paddingLeft: 14, fontWeight: 700, color: T().textPrimary, fontSize: 14 }}>Leftover</td>
                <td style={{ ...cs, fontWeight: 700, fontSize: 14, color: left.c >= 0 ? T().green : T().red }}>{fm(left.c)}</td>
                <td style={{ ...cs, fontWeight: 700, fontSize: 14, color: left.f1 >= 0 ? T().green : T().red }}>{fm(left.f1)}</td>
                <td style={{ ...cs, fontWeight: 700, fontSize: 14, color: left.f2 >= 0 ? T().green : T().red, paddingRight: 14 }}>{fm(left.f2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Annual Projection */}
      <div style={{ background: cardBg, borderRadius: 12, border: cardBd, padding: 14, marginBottom: 12 }}>
        <div style={{ fontSize: 10, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, marginBottom: 6 }}>Annual Projection</div>
        <div style={{ fontSize: 10, color: T().textDim, marginBottom: 10 }}>{extraPay ? "(Leftover x 12) + Extra Paycheck + Bonus" : "(Leftover x 12) + Bonus After Tax"}</div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: T().textMuted, textAlign: "center", marginBottom: 6 }}>Bonus (after tax)</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            <div style={{ textAlign: "center" }}><EC value={income.bonus.current} onChange={function (v) { setIncome(function (prev) { return { ...prev, bonus: { ...prev.bonus, current: v } }; }); }} color="#7ab8d8" /></div>
            <div style={{ textAlign: "center" }}><EC value={income.bonus.future1} onChange={function (v) { setIncome(function (prev) { return { ...prev, bonus: { ...prev.bonus, future1: v } }; }); }} color="#7ab8d8" /></div>
            <div style={{ textAlign: "center" }}><EC value={income.bonus.future2} onChange={function (v) { setIncome(function (prev) { return { ...prev, bonus: { ...prev.bonus, future2: v } }; }); }} color="#7ab8d8" /></div>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          {[{ l: "Current", lm: left.c, sal: income.salary.current, bon: income.bonus.current, t: ann.c }, { l: "Future 1", lm: left.f1, sal: income.salary.future1, bon: income.bonus.future1, t: ann.f1 }, { l: "Future 2", lm: left.f2, sal: income.salary.future2, bon: income.bonus.future2, t: ann.f2 }].map(function (d, i) {
            return (
              <div key={i} style={{ textAlign: "center", background: "rgba(100,160,255,0.04)", borderRadius: 8, padding: "10px 6px" }}>
                <div style={{ fontSize: 10, color: T().textMuted, fontWeight: 600, marginBottom: 6 }}>{d.l}</div>
                <div style={{ fontSize: 9, color: T().textDim, marginBottom: 2 }}>{fm(d.lm)} x 12 = {fm(d.lm * 12)}</div>
                {extraPay && <div style={{ fontSize: 9, color: T().green, marginBottom: 2 }}>+ Extra check: {fm(d.sal)}</div>}
                <div style={{ fontSize: 9, color: T().textDim, marginBottom: 6 }}>+ Bonus: {fm(d.bon)}</div>
                <div style={{ fontSize: 17, fontWeight: 700, fontFamily: "monospace", color: d.t >= 0 ? T().green : T().red }}>{fm(d.t)}</div>
                <div style={{ fontSize: 9, color: T().textDim, marginTop: 2 }}>/year</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Spending Breakdown — pie chart of where the month's money goes.
          Toggle picks which scenario (current/future1/future2). Slices: one per
          bill with amount > 0, one combined "Credit Cards" slice (estimated
          monthly minimum payments), one "Leftover" slice (income minus all
          expenses). Pure inline SVG to avoid pulling in a chart library. */}
      {(function () {
        var scenarioKey = pieScenario === "current" ? "current" : pieScenario === "future1" ? "future1" : "future2";
        var scenarioLabel = pieScenario === "current" ? "Current" : pieScenario === "future1" ? "Future 1" : "Future 2";
        // Income for this scenario
        var sIncome = pieScenario === "current"
          ? income.salary.current + income.va.current
          : pieScenario === "future1"
            ? income.salary.future1 + income.va.future1
            : income.salary.future2 + income.va.future2;
        // ccPay is computed once above; it's the same regardless of scenario
        // because it derives from card balances, not the scenario columns.
        // Build slices
        var slices = [];
        activeBills.slice().sort(function (a, b) { return a.dueDay - b.dueDay; }).forEach(function (b) {
          var amt = b[scenarioKey];
          if (amt > 0) slices.push({ name: b.name, value: amt, kind: "bill" });
        });
        if (ccPay > 0) slices.push({ name: "Credit Cards", value: Math.round(ccPay), kind: "cc" });
        var totalExpense = slices.reduce(function (s, x) { return s + x.value; }, 0);
        var leftover = sIncome - totalExpense;
        if (leftover > 0) slices.push({ name: "Leftover", value: leftover, kind: "leftover" });
        var grandTotal = slices.reduce(function (s, x) { return s + x.value; }, 0);

        // Color palette: bills get cool blues/purples, CC gets red, leftover gets green.
        var billPalette = ["#4a8fe7", "#6ba3d8", "#7a9ab8", "#a98be0", "#8cb5d6", "#5d9cc8", "#9d7fc4", "#6f8db5"];
        function colorFor(slice, billIdx) {
          if (slice.kind === "cc") return T().red;
          if (slice.kind === "leftover") return T().green;
          return billPalette[billIdx % billPalette.length];
        }

        // SVG arc math. Pie centered at (cx, cy), radius r. Slice from angle a0 to a1
        // (in radians, 0 = top of circle, growing clockwise) becomes a path.
        var cx = 110, cy = 110, r = 95;
        function polar(angle) {
          var rad = angle - Math.PI / 2; // shift so 0 is at top
          return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
        }
        function arcPath(a0, a1) {
          var [x0, y0] = polar(a0);
          var [x1, y1] = polar(a1);
          var large = (a1 - a0) > Math.PI ? 1 : 0;
          return "M " + cx + " " + cy + " L " + x0 + " " + y0 + " A " + r + " " + r + " 0 " + large + " 1 " + x1 + " " + y1 + " Z";
        }

        // Build paths
        var paths = [];
        var legendItems = [];
        var billIdx = 0;
        var startAngle = 0;
        slices.forEach(function (slice, i) {
          var fraction = grandTotal > 0 ? slice.value / grandTotal : 0;
          var endAngle = startAngle + fraction * 2 * Math.PI;
          var color = colorFor(slice, slice.kind === "bill" ? billIdx : 0);
          if (slice.kind === "bill") billIdx++;
          // Avoid degenerate single-slice (full circle) — SVG arc can't draw a
          // 360° arc with a single command, so for a single 100% slice draw a
          // simple full circle instead.
          if (slices.length === 1) {
            paths.push(<circle key={i} cx={cx} cy={cy} r={r} fill={color} />);
          } else {
            paths.push(<path key={i} d={arcPath(startAngle, endAngle)} fill={color} stroke={T().bg.includes("gradient") ? "#0a0f1a" : T().cardBg} strokeWidth={1.5} />);
          }
          legendItems.push({ name: slice.name, value: slice.value, color: color, pct: fraction * 100 });
          startAngle = endAngle;
        });

        return (
          <div style={{ background: cardBg, borderRadius: 12, border: cardBd, padding: 14, marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <span style={{ fontSize: 11, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>Spending Breakdown — {scenarioLabel}</span>
              <div style={{ display: "flex", gap: 4, background: T().barTrack, borderRadius: 6, padding: 2 }}>
                {[{ k: "current", l: "Current" }, { k: "future1", l: "Future 1" }, { k: "future2", l: "Future 2" }].map(function (opt) {
                  var active = pieScenario === opt.k;
                  return (
                    <button key={opt.k} onClick={function () { setPieScenario(opt.k); }} style={{ padding: "4px 10px", border: "none", borderRadius: 4, background: active ? T().accent : "transparent", color: active ? "#fff" : T().textDim, fontSize: 10, fontFamily: "system-ui", fontWeight: active ? 600 : 500, cursor: "pointer", transition: "all 0.15s" }}>{opt.l}</button>
                  );
                })}
              </div>
            </div>
            {grandTotal === 0 ? (
              <div style={{ fontSize: 12, color: T().textDim, padding: "20px 0", textAlign: "center" }}>No data to display for {scenarioLabel}.</div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center" }}>
                <svg width={220} height={220} viewBox="0 0 220 220" style={{ flexShrink: 0 }}>
                  {paths}
                </svg>
                <div style={{ flex: 1, minWidth: 200, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 16px" }}>
                  {legendItems.map(function (item, i) {
                    return (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: T().textPrimary }}>
                        <span style={{ width: 10, height: 10, borderRadius: 2, background: item.color, flexShrink: 0 }} />
                        <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.name}</span>
                        <span style={{ fontFamily: "monospace", color: T().textDim, fontSize: 10 }}>{item.pct.toFixed(0)}%</span>
                        <span style={{ fontFamily: "monospace", color: T().textPrimary, fontSize: 11 }}>{fm(item.value)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Credit Cards */}
      <div style={{ background: cardBg, borderRadius: 12, border: cardBd, overflow: "hidden" }}>
        <div style={{ padding: "12px 14px 4px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 11, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>Credit Cards</span>
          <span style={{ fontSize: 10, color: T().textDim }}>Bal: <span style={{ color: T().red, fontFamily: "monospace" }}>{fm(tBal)}</span> | Int/mo: <span style={{ color: T().red, fontFamily: "monospace" }}>{fm(tInt)}</span></span>
        </div>
        <div style={{ padding: "4px 14px 8px" }}>
          <div style={{ height: 4, background: T().barTrack, borderRadius: 2, overflow: "hidden" }}>
            <div style={{ height: "100%", borderRadius: 2, background: utilColor(tBal / tLim), width: Math.min((tBal / tLim) * 100, 100) + "%", transition: "width 0.3s" }} />
          </div>
          <div style={{ fontSize: 9, color: T().textDim, marginTop: 2, textAlign: "right" }}>{fp(tBal / tLim)} of {fm(tLim)}</div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 500 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(100,160,255,0.08)" }}>
                <th style={{ ...hs, textAlign: "left", paddingLeft: 14 }}>Card</th>
                <th style={hs}>Min Pmt</th>
                <th style={hs}>Balance</th>
                <th style={hs}>Limit</th>
                <th style={hs}>Used</th>
                <th style={hs}>Mode</th>
                <th style={{ ...hs, paddingRight: 14 }}>28% Gap</th>
              </tr>
            </thead>
            <tbody>
              {displayCards.map(function (c, idx) {
                var u = c.limit > 0 ? c.balance / c.limit : 0;
                var t28 = Math.round(c.limit * 0.28);
                var over = c.balance > t28 && c.balance > 0;
                var est = estPayment(c.balance, c.apr, c.minPct, c.zero);
                var isProjected = projectedBals && projectedBals[c.id] !== undefined && projectedBals[c.id] !== cards.find(function (x) { return x.id === c.id; }).balance;
                return (
                  <tr key={c.id} style={{ borderBottom: rowBd, background: idx % 2 === 0 ? rowEven : rowOdd }}>
                    <td style={{ padding: "6px 4px 6px 14px", fontSize: 12, color: T().headerColor, textAlign: "left" }}>
                      <div style={{ fontWeight: 500 }}>{c.name}</div>
                      <div style={{ fontSize: 9, color: T().textDim }}>{ordinal(c.dueDay)} | {c.zero ? "0%" : c.apr + "%"}</div>
                    </td>
                    <td style={{ ...cs, fontSize: 12, color: isProjected ? T().orange : "#5a7a9a" }}>{est > 0 ? fm(est) : "-"}{isProjected ? " *" : ""}</td>
                    <td style={{ ...cs, fontSize: 12 }}>
                      {isProjected ? <span style={{ color: T().orange }}>{fm(c.balance)} *</span> : <EC value={cards.find(function (x) { return x.id === c.id; }).balance} onChange={function (v) { setCards(function (prev) { return prev.map(function (x) { return x.id === c.id ? { ...x, balance: v } : x; }); }); }} />}
                    </td>
                    <td style={{ ...cs, fontSize: 12, color: T().textMuted }}>{fm(c.limit)}</td>
                    <td style={{ ...cs, fontSize: 12, color: utilColor(u) }}>{fp(u)}</td>
                    <td style={{ ...cs, fontSize: 10, textAlign: "center" }}>
                      <button onClick={function () { setCards(function (prev) { return prev.map(function (x) { return x.id === c.id ? { ...x, mode: c.mode === "paydown" ? "used" : "paydown" } : x; }); }); }} style={{ background: c.mode === "paydown" ? "rgba(92,184,138,0.12)" : "rgba(196,144,96,0.12)", border: "1px solid " + (c.mode === "paydown" ? "rgba(92,184,138,0.25)" : "rgba(196,144,96,0.25)"), borderRadius: 4, color: c.mode === "paydown" ? T().green : T().orange, padding: "2px 6px", cursor: "pointer", fontSize: 9, fontFamily: "system-ui", fontWeight: 600 }}>{c.mode === "paydown" ? "Paying" : "Active"}</button>
                    </td>
                    <td style={{ ...cs, fontSize: 11, paddingRight: 14, color: over ? T().red : T().green }}>{c.balance === 0 ? "-" : over ? "-" + fm(c.balance - t28) : "OK"}</td>
                  </tr>
                );
              })}
              <tr style={{ borderTop: secBd, background: "rgba(100,160,255,0.04)" }}>
                <td style={{ padding: "7px 4px 7px 14px", fontSize: 12, fontWeight: 700, color: T().textSecondary, textAlign: "left" }}>TOTAL</td>
                <td style={{ ...cs, fontSize: 12, fontWeight: 700 }}>{fm(ccPay)}</td>
                <td style={{ ...cs, fontSize: 12, fontWeight: 700, color: T().red }}>{fm(tBal)}</td>
                <td style={{ ...cs, fontSize: 12, color: T().textMuted }}>{fm(tLim)}</td>
                <td style={{ ...cs, fontSize: 12, fontWeight: 700, color: utilColor(tBal / tLim) }}>{fp(tBal / tLim)}</td>
                <td style={cs}></td>
                <td style={{ ...cs, paddingRight: 14 }}>-</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* CALENDAR TAB */
function CalendarTab() {
  var ctx = useBudget();
  var bills = ctx.bills;
  var income = ctx.income;
  var cards = ctx.cards, setCards = ctx.setCards;
  var payroll = ctx.payroll;
  var oneTimeTxs = ctx.oneTimeTxs, setOneTimeTxs = ctx.setOneTimeTxs;
  var masterVaChanges = ctx.vaChanges;
  var masterSalaryChanges = ctx.salaryChanges;
  var startingBalance = ctx.startingBalance;
  var savingsBalance = ctx.savingsBalance || 0;
  var scenarios = ctx.scenarios || [];
  var setScenarios = ctx.setScenarios;
  var activeScenarioId = ctx.activeScenarioId;
  var setActiveScenarioId = ctx.setActiveScenarioId;

  // Resolve the active scenario (or null for Master view). If the id is set
  // but the scenario doesn't exist (deleted mid-render, restored backup
  // without it, etc.), fall through to Master — the activeScenarioId state
  // is transient and will be cleaned up on the next tab switch or reload.
  var activeScenario = null;
  if (activeScenarioId !== null && activeScenarioId !== undefined) {
    for (var si = 0; si < scenarios.length; si++) {
      if (scenarios[si].id === activeScenarioId) { activeScenario = scenarios[si]; break; }
    }
  }

  // Effective baselines and scheduled-change arrays for calendar computation.
  // When viewing a scenario, all income inputs (salary, VA, and their scheduled
  // changes) come from the scenario. Bills, cards, payroll, oneTimeTxs, and
  // balances always come from Master regardless of scenario.
  var baselineVA = activeScenario ? (activeScenario.income ? activeScenario.income.va || 0 : 0) : income.va.current;
  var baselineSalary = activeScenario ? (activeScenario.income ? activeScenario.income.salary || 0 : 0) : income.salary.current;
  var vaChanges = activeScenario ? (activeScenario.vaChanges || []) : masterVaChanges;
  var salaryChanges = activeScenario ? (activeScenario.salaryChanges || []) : masterSalaryChanges;
  // Convert a monthly salary amount to per-paycheck amount based on payroll
  // frequency. Used for both baseline and date-effective salary lookups.
  function salaryToPaycheck(monthlySalary) {
    if (payroll.frequency === "monthly") return monthlySalary;
    if (payroll.frequency === "weekly") return Math.round(monthlySalary / 4);
    // biweekly and bimonthly both pay twice a month on average
    return Math.round(monthlySalary / 2);
  }
  // Effective per-paycheck amount on a given date — looks up the active
  // salary scheduled change (if any) and converts to per-paycheck.
  function payAmtFor(date) {
    var monthly = effectiveSalary(date, baselineSalary, salaryChanges);
    return salaryToPaycheck(monthly);
  }

  // Create a new scenario, seeded with copies of Master's current income
  // config. The user can then edit the copies without affecting Master.
  // ID generation defends against same-millisecond collisions (Date.now()
  // alone can collide on rapid double-clicks).
  function addScenario() {
    var nextId = Date.now();
    scenarios.forEach(function (s) { if (s.id >= nextId) nextId = s.id + 1; });
    // Default name: pick "Scenario N" where N avoids collision with existing
    // names matching that exact pattern. User can rename freely.
    var maxN = 0;
    scenarios.forEach(function (s) {
      var m = /^Scenario (\d+)$/.exec(s.name || "");
      if (m) { var n = parseInt(m[1], 10); if (n > maxN) maxN = n; }
    });
    var defaultName = "Scenario " + Math.max(maxN + 1, scenarios.length + 1);
    // Deep copy the scheduled changes with fresh ids. Re-using Master's ids
    // would be functionally fine (scoping is per-array) but regenerating is
    // cleaner — it eliminates any confusion if a future operation merges
    // arrays for any reason, and avoids the user seeing the same id in two
    // places when debugging.
    var copiedSalaryChanges = (masterSalaryChanges || []).map(function (c, i) {
      return { id: nextId + 1 + i, effectiveDate: c.effectiveDate, newAmount: c.newAmount };
    });
    var copiedVaChanges = (masterVaChanges || []).map(function (c, i) {
      return { id: nextId + 1 + copiedSalaryChanges.length + i, effectiveDate: c.effectiveDate, newAmount: c.newAmount };
    });
    var newScenario = {
      id: nextId,
      name: defaultName,
      income: { salary: income.salary.current || 0, va: income.va.current || 0 },
      salaryChanges: copiedSalaryChanges,
      vaChanges: copiedVaChanges,
    };
    setScenarios(function (prev) { return [...(prev || []), newScenario]; });
    setActiveScenarioId(nextId);
  }

  // Delete a scenario. If it's the active one, reset to Master view so the
  // tab strip and inputs don't reference a gone-but-still-active id.
  function deleteScenario(id) {
    setScenarios(function (prev) { return (prev || []).filter(function (s) { return s.id !== id; }); });
    if (activeScenarioId === id) setActiveScenarioId(null);
  }

  // Apply an in-place update to the active scenario. The updater receives the
  // current scenario object and returns the new one. Routed through
  // setScenarios so the autoSave timing rules of makeUpdater apply.
  function updateActiveScenario(updater) {
    if (activeScenarioId === null || activeScenarioId === undefined) return;
    setScenarios(function (prev) {
      return (prev || []).map(function (s) {
        return s.id === activeScenarioId ? updater(s) : s;
      });
    });
  }

  const [showAddTx, setShowAddTx] = useState(false);
  const [txDate, setTxDate] = useState(new Date().toISOString().split("T")[0]);
  const [txAmt, setTxAmt] = useState("");
  const [txNote, setTxNote] = useState("");
  const [txIsDeposit, setTxIsDeposit] = useState(true);
  const [txCardId, setTxCardId] = useState("");
  const [viewRange, setViewRange] = useState("2w");
  const [otFilter, setOtFilter] = useState("upcoming");

  // Scenario edit panel local state. Mirrors the Settings tab patterns for
  // scheduled VA/salary changes but is scoped to the active scenario.
  const [scShowAddSalaryChange, setScShowAddSalaryChange] = useState(false);
  const [scNewSalaryDate, setScNewSalaryDate] = useState("");
  const [scNewSalaryAmount, setScNewSalaryAmount] = useState("");
  const [scConfirmDelSalaryChange, setScConfirmDelSalaryChange] = useState(null);
  const [scShowAddVaChange, setScShowAddVaChange] = useState(false);
  const [scNewVaDate, setScNewVaDate] = useState("");
  const [scNewVaAmount, setScNewVaAmount] = useState("");
  const [scConfirmDelVaChange, setScConfirmDelVaChange] = useState(null);
  const [scConfirmDelete, setScConfirmDelete] = useState(false);
  // Which day's transaction tooltip is open, keyed by local YYYY-MM-DD.
  // Desktop: driven by hover. Mobile: tap toggles (no hover on touch).
  const [tooltipDay, setTooltipDay] = useState(null);

  var rangeWeeks = { "2w": 2, "1m": 5, "3m": 13, "6m": 26, "1y": 52, "2y": 104, "3y": 156 };
  var totalWeeksNeeded = rangeWeeks[viewRange] || 2;
  // PAST_WEEKS controls how far back the user can scroll. The full weeks[]
  // array spans (160 + PAST_WEEKS) weeks — enough to cover the largest view
  // range (3y = 156 weeks) plus scroll slack. Today's week sits at index
  // PAST_WEEKS.
  var PAST_WEEKS = 4;
  var TOTAL_WEEKS = 160 + PAST_WEEKS;

  var otByDate = {};
  oneTimeTxs.forEach(function (tx) {
    var d = new Date(tx.date + "T12:00:00");
    var key = d.getFullYear() + "-" + d.getMonth() + "-" + d.getDate();
    if (!otByDate[key]) otByDate[key] = [];
    otByDate[key].push({ amount: tx.amount, note: tx.note, type: "onetime", cardId: tx.cardId || null });
  });

  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 640);
  useEffect(function () {
    function handleResize() { setIsDesktop(window.innerWidth > 640); }
    window.addEventListener("resize", handleResize);
    return function () { window.removeEventListener("resize", handleResize); };
  }, []);

  var calData = useMemo(function () {
    var result = [];
    var now = new Date();
    var todayMidnight = new Date(now); todayMidnight.setHours(0, 0, 0, 0);
    var currentSunday = new Date(now);
    currentSunday.setDate(now.getDate() - now.getDay());
    currentSunday.setHours(0, 0, 0, 0);
    // Walk-start = PAST_WEEKS Sundays before current Sunday.
    var start = new Date(currentSunday);
    start.setDate(currentSunday.getDate() - PAST_WEEKS * 7);
    var end = new Date(start);
    end.setDate(end.getDate() + TOTAL_WEEKS * 7);

    // Track card balances for paydown simulation
    var cardBals = {};
    cards.forEach(function (c) { cardBals[c.id] = c.balance; });

    // Track which cards had one-time payments and when (to skip regular payments within 30 days)
    var cardSkipDates = {};

    // Build monthly recurring items — multiple items can share the same day.
    // VA item is a template; its amount is resolved per-month inside the loop
    // below so that scheduled VA changes (effective on a future date) take
    // effect on the right month rather than being a static value.
    var items = [];
    items.push({ day: 1, amount: 0, note: "VA Payment", type: "income", cardId: null, vaTemplate: true });
    bills.forEach(function (b) {
      if (b.current !== 0) items.push({ day: b.dueDay, amount: -Math.abs(b.current), note: b.name, type: "bill", cardId: null, startDate: b.startDate || null, endDate: b.endDate || null });
    });
    cards.forEach(function (c) {
      if (c.balance > 0) {
        items.push({ day: c.dueDay, amount: 0, note: c.name, type: "cc", cardId: c.id });
      }
    });

    var monthlyItems = {};
    items.forEach(function (item) {
      if (!monthlyItems[item.day]) monthlyItems[item.day] = [];
      monthlyItems[item.day].push(item);
    });

    var payDates = getPayDates(payroll, start, end);
    var payDateKeys = {};
    payDates.forEach(function (d) {
      payDateKeys[d.getFullYear() + "-" + d.getMonth() + "-" + d.getDate()] = true;
    });

    // Compute the seed balance at `start` by reverse-summing events from today
    // back to start. We anchor on user-entered `startingBalance` being the
    // balance "as of the start of today" and walk backward through each past
    // day, undoing its events. Card payment events are intentionally skipped
    // here because their amounts depend on simulated card balances we cannot
    // reliably reconstruct from a past point. This means the deeper into the
    // past you scroll, the more the displayed balance drifts from reality —
    // but for ~4 weeks the drift is bounded and these numbers are explicitly
    // marked as projections via faded styling.
    var pastSeed = startingBalance || 0;
    var cursor = new Date(todayMidnight);
    cursor.setDate(cursor.getDate() - 1);
    while (cursor >= start) {
      var dom = cursor.getDate();
      var dKey = cursor.getFullYear() + "-" + cursor.getMonth() + "-" + cursor.getDate();
      // Reverse paycheck (subtract)
      if (payDateKeys[dKey]) pastSeed -= payAmtFor(cursor);
      // Reverse VA (subtract) — only if it's day-of-month 1
      if (dom === 1) {
        var vaForDay = effectiveVA(cursor, baselineVA, vaChanges);
        if (vaForDay > 0) pastSeed -= vaForDay;
      }
      // Reverse bills (add back since they were withdrawals)
      if (monthlyItems[dom]) {
        monthlyItems[dom].forEach(function (item) {
          if (item.vaTemplate) return; // handled above
          if (item.type === "bill") {
            // Mirror the forward-loop active-range rule: a bill outside its
            // [startDate, endDate] range on this past date never occurred,
            // so there's nothing to undo.
            var curStr = localISO(cursor);
            if (item.startDate && curStr < item.startDate) return;
            if (item.endDate && curStr > item.endDate) return;
            pastSeed -= item.amount; // amount is negative; subtracting negates
          }
          // CC items have amount 0 in the template, so nothing to undo
        });
      }
      // Reverse one-time txs landing on this day (non-card-linked only)
      if (otByDate[dKey]) {
        otByDate[dKey].forEach(function (ot) {
          if (!ot.cardId) pastSeed -= ot.amount; // amount sign already correct, subtract to undo
        });
      }
      cursor.setDate(cursor.getDate() - 1);
    }

    var bal = pastSeed;
    var cardBalSnapshots = {};
    var lastMonth = -1;

    for (var w = 0; w < TOTAL_WEEKS; w++) {
      var wk = [];
      for (var d = 0; d < 7; d++) {
        var dt = new Date(start);
        dt.setDate(start.getDate() + w * 7 + d);
        var dom = dt.getDate();
        var curMonth = dt.getMonth();
        var curYear = dt.getFullYear();
        var monthKey = curYear + "-" + curMonth;
        var dateKey = curYear + "-" + curMonth + "-" + dt.getDate();
        var dayEvs = [];

        // New month: apply interest to paydown cards and capture snapshot
        if (curMonth !== lastMonth) {
          if (lastMonth !== -1) {
            cards.forEach(function (c) {
              if (c.mode === "paydown" && cardBals[c.id] > 0 && c.apr > 0 && !c.zero) {
                var monthlyInt = cardBals[c.id] * (c.apr / 100 / 12);
                cardBals[c.id] += monthlyInt;
              }
            });
          }
          // Snapshot card balances at start of each month
          var snapKey = curYear + "-" + (curMonth < 10 ? "0" : "") + curMonth;
          var snap = {};
          cards.forEach(function (c) { snap[c.id] = Math.round(cardBals[c.id]); });
          cardBalSnapshots[snapKey] = snap;
        }
        lastMonth = curMonth;

        // Process one-time transactions FIRST so card balances update before regular payments
        if (otByDate[dateKey]) {
          // Per option B: c.balance is the user-maintained "as of right now" baseline.
          // Past-dated card txs are pure historical records — the user already
          // accounted for them by setting the balance directly, so skip them here
          // to avoid double-counting. Today's and future txs ARE applied in the
          // simulation so projections reflect them. Bank balance updates for ALL
          // dates regardless, since the running bank total tracks actual cash flow.
          var todayMidnight = new Date();
          todayMidnight.setHours(0, 0, 0, 0);
          var applyToCardBal = dt >= todayMidnight;
          otByDate[dateKey].forEach(function (ot) {
            if (ot.cardId) {
              var payoffAmt = Math.abs(ot.amount);
              if (applyToCardBal) {
                var prevBal = cardBals[ot.cardId] || 0;
                cardBals[ot.cardId] = Math.max(0, prevBal - payoffAmt);
              }
              // Mark this card to skip regular payments within 30 days regardless,
              // since the user paid this card off-cycle either way.
              if (!cardSkipDates[ot.cardId]) cardSkipDates[ot.cardId] = [];
              cardSkipDates[ot.cardId].push(dt.getTime());
              var linkedCard = cards.find(function (c) { return String(c.id) === String(ot.cardId); });
              var isZeroPaydown = linkedCard && linkedCard.zero && linkedCard.mode === "paydown";
              dayEvs.push({ amount: -payoffAmt, note: ot.note + " (bal: " + fm(Math.round(cardBals[ot.cardId] || 0)) + ")", type: "onetime", zeroApr: isZeroPaydown });
            } else {
              dayEvs.push({ amount: ot.amount, note: ot.note, type: "onetime", zeroApr: false });
            }
          });
        }

        if (payDateKeys[dateKey]) {
          dayEvs.push({ amount: payAmtFor(dt), note: "Paycheck", type: "income" });
        }

        if (monthlyItems[dom]) {
          monthlyItems[dom].forEach(function (item) {
            if (item.vaTemplate) {
              var vaForMonth = effectiveVA(dt, baselineVA, vaChanges);
              if (vaForMonth > 0) {
                dayEvs.push({ amount: vaForMonth, note: "VA Payment", type: "income" });
              }
              return;
            }
            if (item.cardId) {
              var card = cards.find(function (c) { return c.id === item.cardId; });
              if (!card) return;
              // Skip regular payment if a one-time payment covered this card within 30 days
              if (cardSkipDates[card.id]) {
                var shouldSkip = false;
                var nowTime = dt.getTime();
                cardSkipDates[card.id].forEach(function (skipTime) {
                  if (Math.abs(nowTime - skipTime) <= 30 * 24 * 60 * 60 * 1000) shouldSkip = true;
                });
                if (shouldSkip) return;
              }
              var curBal = cardBals[card.id];
              if (curBal <= 0) return;
              var pmt = estPayment(curBal, card.apr, card.minPct, card.zero);
              if (card.mode === "paydown") {
                pmt = Math.min(pmt, curBal);
                cardBals[card.id] = Math.max(0, curBal - pmt);
              }
              if (pmt > 0) {
                dayEvs.push({ amount: -pmt, note: item.note + (card.mode === "paydown" ? " (bal: " + fm(Math.round(cardBals[card.id])) + ")" : ""), type: "cc", zeroApr: false });
              }
            } else {
              // Bill active range: fires from start date through end date
              // (both inclusive, both optional). Lexicographic compare is
              // safe for zero-padded YYYY-MM-DD.
              var dtStr = localISO(dt);
              if (item.startDate && dtStr < item.startDate) return;
              if (item.endDate && dtStr > item.endDate) return;
              dayEvs.push({ amount: item.amount, note: item.note, type: item.type });
            }
          });
        }

        if (dayEvs.length > 0) {
          for (var e = 0; e < dayEvs.length; e++) bal += dayEvs[e].amount;
          wk.push({ date: new Date(dt), balance: bal, txs: dayEvs });
        } else {
          wk.push({ date: new Date(dt), balance: bal, txs: null });
        }
      }
      result.push(wk);
    }
    return { weeks: result, projectedBals: cardBals, snapshots: cardBalSnapshots };
  }, [bills, income, cards, baselineSalary, salaryChanges, baselineVA, vaChanges, payroll, oneTimeTxs, otByDate, startingBalance]);

  var weeks = calData.weeks;
  var projectedBals = calData.projectedBals;
  var cardBalSnapshots = calData.snapshots;

  const [startWeek, setStartWeek] = useState(PAST_WEEKS);
  var projDate = ctx.projDate;
  // When projDate changes, jump to the week containing it. When projDate is
  // cleared, jump back to today (week 0). Compute target week as the number
  // of full weeks between today's Sunday and the target date's Sunday.
  useEffect(function () {
    if (!projDate) {
      setStartWeek(PAST_WEEKS);
      return;
    }
    var target = new Date(projDate + "T12:00:00");
    if (isNaN(target.getTime())) return;
    var todayLocal = new Date();
    todayLocal.setHours(0, 0, 0, 0);
    var currentSunday = new Date(todayLocal);
    currentSunday.setDate(todayLocal.getDate() - todayLocal.getDay());
    var targetSunday = new Date(target);
    targetSunday.setHours(0, 0, 0, 0);
    targetSunday.setDate(target.getDate() - target.getDay());
    var diffMs = targetSunday.getTime() - currentSunday.getTime();
    // diffMs is target-relative-to-current. Convert to absolute index in the
    // weeks[] array, where index PAST_WEEKS is the current week.
    var weekIdx = PAST_WEEKS + Math.round(diffMs / (7 * 24 * 60 * 60 * 1000));
    var maxStart = Math.max(0, TOTAL_WEEKS - totalWeeksNeeded);
    if (weekIdx < 0) weekIdx = 0;
    if (weekIdx > maxStart) weekIdx = maxStart;
    setStartWeek(weekIdx);
  }, [projDate, totalWeeksNeeded, PAST_WEEKS, TOTAL_WEEKS]);

  var today = new Date();
  today.setHours(0, 0, 0, 0);
  var visibleWeeks = weeks.slice(startWeek, startWeek + totalWeeksNeeded);
  var maxVisible = 6;
  var scrollableWeeks = visibleWeeks;

  var prevEnd = startWeek > 0 ? weeks[startWeek - 1][6].balance : visibleWeeks[0] ? visibleWeeks[0][0].balance : 0;
  var lastWk = visibleWeeks[visibleWeeks.length - 1];
  var endBal = lastWk ? lastWk[6].balance : 0;
  var change = endBal - prevEnd;

  var allTx = [];
  visibleWeeks.forEach(function (wk) {
    if (!wk) return;
    wk.forEach(function (day) {
      if (day.txs) day.txs.forEach(function (tx) { allTx.push({ ...tx, date: day.date }); });
    });
  });

  var stepSize = Math.max(2, totalWeeksNeeded);

  var todayStr = new Date().toISOString().split("T")[0];
  var filteredOtTxs = oneTimeTxs.filter(function (tx) {
    if (otFilter === "upcoming") return tx.date >= todayStr;
    if (otFilter === "past") return tx.date < todayStr;
    return true;
  }).sort(function (a, b) { return a.date.localeCompare(b.date); });

  // Negative-balance analysis. Walks the FULL weeks array (not just the
  // currently-visible window) so the banner reflects every projected dip
  // across the entire 56-week horizon, regardless of how the user has
  // scrolled. Each week is flagged if any day in it ends negative; the
  // first negative date is used as the banner's jump target.
  // Also flags "low" weeks: any week whose lowest day ends between 0 and
  // 999 (inclusive of 0). These get an orange tint as a visual heads-up
  // for aggressive payoff planning. Red wins over orange when both apply.
  var negativeWeeks = {};
  var lowWeeks = {};
  var negativeDates = [];
  var firstNegativeDate = null;
  var LOW_THRESHOLD = 1000;
  weeks.forEach(function (wk, wIdx) {
    if (!wk) return;
    // Skip past weeks — projected past balances aren't actionable, so they
    // shouldn't trigger banners or week tints.
    if (wIdx < PAST_WEEKS) return;
    var weekHasNegative = false;
    var weekHasLow = false;
    wk.forEach(function (day) {
      if (day.balance < 0) {
        weekHasNegative = true;
        negativeDates.push(day.date);
        if (!firstNegativeDate) firstNegativeDate = day.date;
      } else if (day.balance < LOW_THRESHOLD) {
        weekHasLow = true;
      }
    });
    if (weekHasNegative) negativeWeeks[wIdx] = true;
    else if (weekHasLow) lowWeeks[wIdx] = true;
  });

  function renderWeek(wk, idx, prevWk, absoluteIdx) {
    if (!wk) return null;
    // Check if year changed from previous week
    var showYearDivider = false;
    if (prevWk) {
      var prevYear = prevWk[6].date.getFullYear();
      var curYear = wk[0].date.getFullYear();
      if (curYear !== prevYear) showYearDivider = true;
    }
    // A week is "past" if its absolute index is before the current-week
    // index (PAST_WEEKS). Past weeks display with reduced opacity and
    // suppress severity tinting since negative/low-balance flags aren't
    // actionable retroactively.
    var isPastWeek = absoluteIdx !== undefined && absoluteIdx < PAST_WEEKS;
    var weekHasNegative = !isPastWeek && absoluteIdx !== undefined && negativeWeeks[absoluteIdx];
    var weekHasLow = !isPastWeek && absoluteIdx !== undefined && lowWeeks[absoluteIdx];
    // Severity tier overrides the alternating zebra background. Red (negative)
    // outranks orange (sub-$1000) outranks the default zebra.
    var weekBg, weekBd, weekLabelColor, weekLabelWeight, weekLabelSuffix;
    if (weekHasNegative) {
      weekBg = "rgba(196,92,92,0.15)";
      weekBd = "1px solid rgba(196,92,92,0.45)";
      weekLabelColor = T().red;
      weekLabelWeight = 700;
      weekLabelSuffix = " — negative balance";
    } else if (weekHasLow) {
      weekBg = "rgba(196,144,96,0.15)";
      weekBd = "1px solid rgba(196,144,96,0.40)";
      weekLabelColor = T().orange;
      weekLabelWeight = 700;
      weekLabelSuffix = " — below $1,000";
    } else {
      weekBg = idx % 2 === 0 ? rowEven : rowOdd;
      weekBd = "1px solid transparent";
      weekLabelColor = T().textMuted;
      weekLabelWeight = 600;
      weekLabelSuffix = isPastWeek ? " — past (projection)" : "";
    }
    return (
      <div key={idx} style={{ opacity: isPastWeek ? 0.55 : 1 }}>
        {showYearDivider && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "12px 0", padding: "8px 0" }}>
            <div style={{ flex: 1, height: 1, background: "rgba(74,143,231,0.2)" }} />
            <div style={{ fontSize: 14, fontWeight: 700, color: T().accent, fontFamily: "monospace", letterSpacing: "0.05em" }}>{wk[0].date.getFullYear()}</div>
            <div style={{ flex: 1, height: 1, background: "rgba(74,143,231,0.2)" }} />
          </div>
        )}
        <div style={{ marginBottom: 8, background: weekBg, border: weekBd, borderRadius: 8, padding: "6px 4px" }}>
          <div style={{ fontSize: 10, color: weekLabelColor, fontWeight: weekLabelWeight, marginBottom: 4, paddingLeft: 2 }}>{fd(wk[0].date)} - {fd(wk[6].date)} <span style={{ fontWeight: 700 }}>{wk[0].date.getFullYear()}</span>{weekLabelSuffix}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3 }}>
            {wk.map(function (day, i) {
              var isT = day.date.toDateString() === today.toDateString();
              var hasTx = day.txs !== null;
              var dayTot = hasTx ? day.txs.reduce(function (s, t) { return s + t.amount; }, 0) : 0;
              var hasZeroApr = hasTx && day.txs.some(function (t) { return t.zeroApr === true; });
              var bgColor = isT ? "rgba(74,143,231,0.1)" : hasZeroApr ? "rgba(230,200,80,0.08)" : T().dayCellBg;
              var bdColor = isT ? "rgba(74,143,231,0.3)" : hasZeroApr ? "rgba(230,200,80,0.25)" : T().dayCellBd;

              // Separate deposits and withdrawals for the day
              var deposits = hasTx ? day.txs.filter(function (t) { return t.amount > 0; }) : [];
              var withdrawals = hasTx ? day.txs.filter(function (t) { return t.amount < 0; }) : [];
              var depositTotal = deposits.reduce(function (s, t) { return s + t.amount; }, 0);
              var withdrawalTotal = withdrawals.reduce(function (s, t) { return s + t.amount; }, 0);

              var dayKey = localISO(day.date);
              var showTip = hasTx && tooltipDay === dayKey;

              return (
                <div
                  key={i}
                  onMouseEnter={isDesktop && hasTx ? function () { setTooltipDay(dayKey); } : undefined}
                  onMouseLeave={isDesktop ? function () { setTooltipDay(function (cur) { return cur === dayKey ? null : cur; }); } : undefined}
                  onClick={!isDesktop ? function () { setTooltipDay(function (cur) { return hasTx ? (cur === dayKey ? null : dayKey) : null; }); } : undefined}
                  style={{ position: "relative", cursor: hasTx ? "pointer" : "default", background: bgColor, borderRadius: 7, padding: isDesktop ? "6px 4px" : "4px 2px", minHeight: isDesktop ? 88 : 72, border: "1px solid " + bdColor, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between", textAlign: "center" }}
                >
                  {/* Transaction detail tooltip. Anchored above the cell;
                      horizontal alignment keyed to day-of-week so edge
                      columns don't overflow the viewport (Sun/Mon hug left,
                      Fri/Sat hug right, midweek centers). pointerEvents:none
                      so it never traps the cursor and breaks mouseleave. */}
                  {showTip && (
                    <div style={{ position: "absolute", bottom: "100%", marginBottom: 4, ...(i <= 1 ? { left: 0 } : i >= 5 ? { right: 0 } : { left: "50%", transform: "translateX(-50%)" }), zIndex: 50, background: T().inputBg, border: "1px solid " + T().inputBd, borderRadius: 8, padding: "8px 10px", minWidth: 150, maxWidth: 210, boxShadow: "0 4px 16px rgba(0,0,0,0.45)", textAlign: "left", pointerEvents: "none" }}>
                      <div style={{ fontSize: 9, color: T().textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, whiteSpace: "nowrap" }}>{fd(day.date)} <span style={{ fontWeight: 700 }}>{day.date.getFullYear()}</span></div>
                      {day.txs.map(function (t, ti) {
                        return (
                          <div key={ti} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 10, lineHeight: 1.6 }}>
                            <span style={{ color: T().textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.note || (t.amount >= 0 ? "Deposit" : "Withdrawal")}</span>
                            <span style={{ fontFamily: "monospace", fontWeight: 700, color: t.amount >= 0 ? T().green : t.zeroApr ? T().yellow : T().red, whiteSpace: "nowrap" }}>{(t.amount >= 0 ? "+" : "") + fm(t.amount)}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ fontSize: isDesktop ? 13 : 10, fontWeight: isT ? 700 : 500, color: isT ? T().accent : hasZeroApr ? T().yellow : T().textMuted }}>{day.date.getDate()}</div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0, width: "100%", overflow: "hidden" }}>
                    <div style={{ fontSize: isDesktop ? 14 : 10, fontWeight: 700, color: T().textPrimary, fontFamily: "monospace", whiteSpace: "nowrap" }}>{fm(day.balance)}</div>
                    {savingsBalance > 0 && <div style={{ fontSize: isDesktop ? 10 : 8, fontWeight: 400, color: T().textDim, fontFamily: "monospace", lineHeight: 1.1, whiteSpace: "nowrap" }}>{fm(savingsBalance)}</div>}
                  </div>
                  <div style={{ minHeight: isDesktop ? 24 : 18, display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>
                    {depositTotal > 0 && <div style={{ fontSize: isDesktop ? 11 : 7, fontWeight: 700, fontFamily: "monospace", color: T().green, lineHeight: 1.3 }}>+{fm(depositTotal)}</div>}
                    {withdrawalTotal < 0 && <div style={{ fontSize: isDesktop ? 11 : 7, fontWeight: 700, fontFamily: "monospace", color: hasZeroApr ? T().yellow : T().red, lineHeight: 1.3 }}>{fm(withdrawalTotal)}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "0 12px 130px" }}>
      {/* Scenario tab strip — Master + each scenario + Add button. Master is
          always present and can't be renamed/deleted. activeScenarioId is
          transient (resets on reload) so the Calendar always starts on
          Master. Bills/Cards/Settings/Debt are unaffected by this — they
          always read Master data. */}
      <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 10, overflowX: "auto", paddingBottom: 4 }}>
        <button
          onClick={function () { setActiveScenarioId(null); }}
          style={{
            padding: "5px 12px", borderRadius: 6,
            border: "1px solid " + (activeScenarioId === null ? "rgba(74,143,231,0.35)" : "rgba(100,160,255,0.08)"),
            background: activeScenarioId === null ? "rgba(74,143,231,0.12)" : "transparent",
            color: activeScenarioId === null ? T().accent : T().textDim,
            cursor: "pointer", fontSize: 12, fontFamily: "system-ui",
            fontWeight: activeScenarioId === null ? 600 : 400, whiteSpace: "nowrap",
          }}
        >Master</button>
        {scenarios.map(function (s) {
          var isActive = activeScenarioId === s.id;
          return (
            <button
              key={s.id}
              onClick={function () { setActiveScenarioId(s.id); }}
              style={{
                padding: "5px 12px", borderRadius: 6,
                border: "1px solid " + (isActive ? "rgba(169,139,224,0.45)" : "rgba(100,160,255,0.08)"),
                background: isActive ? "rgba(169,139,224,0.14)" : "transparent",
                color: isActive ? T().purple : T().textDim,
                cursor: "pointer", fontSize: 12, fontFamily: "system-ui",
                fontWeight: isActive ? 600 : 400, whiteSpace: "nowrap",
              }}
            >{s.name}</button>
          );
        })}
        <button
          onClick={addScenario}
          style={{
            padding: "5px 10px", borderRadius: 6,
            border: "1px dashed rgba(92,184,138,0.35)",
            background: "transparent",
            color: T().green,
            cursor: "pointer", fontSize: 12, fontFamily: "system-ui",
            fontWeight: 600, whiteSpace: "nowrap", marginLeft: 2,
          }}
        >+ Add</button>
      </div>

      {/* Edit Income panel — visible only when a scenario is active. Lets the
          user edit the scenario's income overrides and scheduled changes
          inline without leaving the Calendar tab. Master can't be edited
          here — its income config lives on Bills/Settings as before. */}
      {activeScenario && (
        <div style={{ background: cardBg, borderRadius: 12, border: "1px solid rgba(169,139,224,0.25)", overflow: "hidden", marginBottom: 12 }}>
          <div style={{ padding: "10px 14px 6px", borderBottom: "1px solid rgba(169,139,224,0.15)", background: "rgba(169,139,224,0.06)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 9, color: T().purple, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 3 }}>Editing Scenario</div>
                <TextInput
                  value={activeScenario.name}
                  onChange={function (v) {
                    var name = (v || "").toString().trim() || activeScenario.name;
                    updateActiveScenario(function (s) { return { ...s, name: name }; });
                  }}
                  style={{ width: "100%", maxWidth: 240, padding: "5px 8px", background: T().inputBg, border: "1px solid " + T().inputBd, borderRadius: 6, color: T().textPrimary, fontSize: 14, fontFamily: "system-ui", fontWeight: 600, outline: "none", boxSizing: "border-box" }}
                />
              </div>
              {scConfirmDelete ? (
                <div style={{ display: "flex", gap: 4 }}>
                  <button onClick={function () { setScConfirmDelete(false); }} style={{ background: "transparent", border: "1px solid " + T().inputBd, borderRadius: 4, color: T().textDim, fontSize: 10, padding: "4px 8px", cursor: "pointer", fontFamily: "system-ui" }}>Cancel</button>
                  <button onClick={function () { deleteScenario(activeScenario.id); setScConfirmDelete(false); }} style={{ background: T().red, border: "none", borderRadius: 4, color: "#fff", fontSize: 10, padding: "4px 10px", cursor: "pointer", fontFamily: "system-ui", fontWeight: 600 }}>Delete</button>
                </div>
              ) : (
                <button onClick={function () { setScConfirmDelete(true); }} style={{ background: "transparent", border: "1px solid " + T().inputBd, borderRadius: 4, color: T().red, fontSize: 11, padding: "4px 10px", cursor: "pointer", fontFamily: "system-ui", fontWeight: 500 }}>Delete</button>
              )}
            </div>
          </div>

          <div style={{ padding: "10px 14px 14px" }}>
            {/* Income overrides — flat monthly amounts. Bills/Settings continue
                to manage Master's income; the values here are scenario-only. */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 10, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, marginBottom: 4 }}>Monthly Salary</div>
                <NumInput
                  value={activeScenario.income ? (activeScenario.income.salary || 0) : 0}
                  onChange={function (v) { updateActiveScenario(function (s) { return { ...s, income: { ...(s.income || {}), salary: v } }; }); }}
                  style={{ width: "100%", padding: "7px 10px", background: T().inputBg, border: "1px solid " + T().inputBd, borderRadius: 6, color: T().green, fontSize: 14, fontFamily: "monospace", outline: "none", textAlign: "right", boxSizing: "border-box" }}
                />
              </div>
              <div>
                <div style={{ fontSize: 10, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, marginBottom: 4 }}>Monthly VA</div>
                <NumInput
                  value={activeScenario.income ? (activeScenario.income.va || 0) : 0}
                  onChange={function (v) { updateActiveScenario(function (s) { return { ...s, income: { ...(s.income || {}), va: v } }; }); }}
                  style={{ width: "100%", padding: "7px 10px", background: T().inputBg, border: "1px solid " + T().inputBd, borderRadius: 6, color: T().green, fontSize: 14, fontFamily: "monospace", outline: "none", textAlign: "right", boxSizing: "border-box" }}
                />
              </div>
            </div>

            {/* Salary Scheduled Changes for this scenario. Mirrors Settings tab
                pattern — same { id, effectiveDate, newAmount } shape, future
                dates only. Stored on the scenario, not on Master. */}
            <div style={{ background: "rgba(100,160,255,0.04)", borderRadius: 8, padding: "8px 10px", marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 10, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>Salary Scheduled Changes</span>
                <button onClick={function () { setScShowAddSalaryChange(!scShowAddSalaryChange); setScNewSalaryDate(""); setScNewSalaryAmount(""); }} style={{ background: "rgba(74,143,231,0.1)", border: "1px solid rgba(74,143,231,0.25)", borderRadius: 4, color: T().accent, fontSize: 10, padding: "2px 8px", cursor: "pointer", fontFamily: "system-ui", fontWeight: 600 }}>{scShowAddSalaryChange ? "Cancel" : "+ Add"}</button>
              </div>
              {scShowAddSalaryChange && (
                <div style={{ background: T().inputBg, borderRadius: 6, padding: 8, marginBottom: 8, border: "1px solid " + T().inputBd }}>
                  <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 9, color: T().textMuted, marginBottom: 2 }}>Effective Date</div>
                      <input type="date" min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split("T")[0]} value={scNewSalaryDate} onChange={function (e) { setScNewSalaryDate(e.target.value); }} style={{ width: "100%", padding: "5px 7px", background: T().inputBg, border: "1px solid " + T().inputBd, borderRadius: 5, color: T().textPrimary, fontSize: 11, fontFamily: "monospace", outline: "none", boxSizing: "border-box" }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 9, color: T().textMuted, marginBottom: 2 }}>New Monthly Salary</div>
                      <input type="number" placeholder="0" value={scNewSalaryAmount} onChange={function (e) { setScNewSalaryAmount(e.target.value); }} style={{ width: "100%", padding: "5px 7px", background: T().inputBg, border: "1px solid " + T().inputBd, borderRadius: 5, color: T().textPrimary, fontSize: 11, fontFamily: "monospace", outline: "none", boxSizing: "border-box", textAlign: "right" }} />
                    </div>
                  </div>
                  <button onClick={function () {
                    var amt = parseFloat(scNewSalaryAmount);
                    if (!scNewSalaryDate || !amt || amt < 0) return;
                    var t = new Date(); t.setHours(0, 0, 0, 0);
                    var ed = new Date(scNewSalaryDate + "T12:00:00");
                    if (ed <= t) return;
                    // Generate a unique id within this scenario's salaryChanges.
                    var entryId = Date.now();
                    (activeScenario.salaryChanges || []).forEach(function (c) { if (c.id >= entryId) entryId = c.id + 1; });
                    var entry = { id: entryId, effectiveDate: scNewSalaryDate, newAmount: amt };
                    updateActiveScenario(function (s) { return { ...s, salaryChanges: [...(s.salaryChanges || []), entry] }; });
                    setScNewSalaryDate(""); setScNewSalaryAmount(""); setScShowAddSalaryChange(false);
                  }} style={{ width: "100%", padding: "6px 12px", borderRadius: 5, border: "none", background: T().btnBg, color: T().btnText, cursor: "pointer", fontSize: 11, fontFamily: "system-ui", fontWeight: 600 }}>Save Change</button>
                </div>
              )}
              {(activeScenario.salaryChanges || []).length === 0 ? (
                <div style={{ fontSize: 10, color: T().textDim, fontStyle: "italic", padding: "2px 0" }}>No scheduled changes. Baseline salary of {fm(activeScenario.income ? (activeScenario.income.salary || 0) : 0)}/mo applies indefinitely.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {(activeScenario.salaryChanges || []).slice().sort(function (a, b) { return a.effectiveDate.localeCompare(b.effectiveDate); }).map(function (c, idx) {
                    var d = new Date(c.effectiveDate + "T12:00:00");
                    var dateStr = (d.getMonth() + 1) + "/" + d.getDate() + "/" + d.getFullYear();
                    return (
                      <div key={c.id} style={{ background: idx % 2 === 0 ? rowEven : rowOdd, borderRadius: 5, padding: "6px 8px", display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ flex: 1, fontSize: 11, color: T().textPrimary }}>
                          Starting <span style={{ fontFamily: "monospace", color: T().accent }}>{dateStr}</span>: <span style={{ fontFamily: "monospace", color: T().green, fontWeight: 600 }}>{fm(c.newAmount)}</span>/mo
                        </div>
                        {scConfirmDelSalaryChange === c.id ? (
                          <div style={{ display: "flex", gap: 3 }}>
                            <button onClick={function () { setScConfirmDelSalaryChange(null); }} style={{ background: "transparent", border: "1px solid " + T().inputBd, borderRadius: 4, color: T().textDim, fontSize: 9, padding: "1px 5px", cursor: "pointer", fontFamily: "system-ui" }}>No</button>
                            <button onClick={function () {
                              updateActiveScenario(function (s) { return { ...s, salaryChanges: (s.salaryChanges || []).filter(function (x) { return x.id !== c.id; }) }; });
                              setScConfirmDelSalaryChange(null);
                            }} style={{ background: T().red, border: "none", borderRadius: 4, color: "#fff", fontSize: 9, padding: "1px 5px", cursor: "pointer", fontFamily: "system-ui" }}>Yes</button>
                          </div>
                        ) : (
                          <button onClick={function () { setScConfirmDelSalaryChange(c.id); }} style={{ background: "none", border: "none", color: T().red, cursor: "pointer", fontSize: 13, padding: "0 4px" }}>×</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* VA Scheduled Changes for this scenario. Same shape and rules
                as Master's vaChanges array. */}
            <div style={{ background: "rgba(100,160,255,0.04)", borderRadius: 8, padding: "8px 10px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 10, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>VA Scheduled Changes</span>
                <button onClick={function () { setScShowAddVaChange(!scShowAddVaChange); setScNewVaDate(""); setScNewVaAmount(""); }} style={{ background: "rgba(74,143,231,0.1)", border: "1px solid rgba(74,143,231,0.25)", borderRadius: 4, color: T().accent, fontSize: 10, padding: "2px 8px", cursor: "pointer", fontFamily: "system-ui", fontWeight: 600 }}>{scShowAddVaChange ? "Cancel" : "+ Add"}</button>
              </div>
              {scShowAddVaChange && (
                <div style={{ background: T().inputBg, borderRadius: 6, padding: 8, marginBottom: 8, border: "1px solid " + T().inputBd }}>
                  <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 9, color: T().textMuted, marginBottom: 2 }}>Effective Date</div>
                      <input type="date" min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split("T")[0]} value={scNewVaDate} onChange={function (e) { setScNewVaDate(e.target.value); }} style={{ width: "100%", padding: "5px 7px", background: T().inputBg, border: "1px solid " + T().inputBd, borderRadius: 5, color: T().textPrimary, fontSize: 11, fontFamily: "monospace", outline: "none", boxSizing: "border-box" }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 9, color: T().textMuted, marginBottom: 2 }}>New Amount</div>
                      <input type="number" placeholder="0" value={scNewVaAmount} onChange={function (e) { setScNewVaAmount(e.target.value); }} style={{ width: "100%", padding: "5px 7px", background: T().inputBg, border: "1px solid " + T().inputBd, borderRadius: 5, color: T().textPrimary, fontSize: 11, fontFamily: "monospace", outline: "none", boxSizing: "border-box", textAlign: "right" }} />
                    </div>
                  </div>
                  <button onClick={function () {
                    var amt = parseFloat(scNewVaAmount);
                    if (!scNewVaDate || !amt || amt < 0) return;
                    var t = new Date(); t.setHours(0, 0, 0, 0);
                    var ed = new Date(scNewVaDate + "T12:00:00");
                    if (ed <= t) return;
                    var entryId = Date.now();
                    (activeScenario.vaChanges || []).forEach(function (c) { if (c.id >= entryId) entryId = c.id + 1; });
                    var entry = { id: entryId, effectiveDate: scNewVaDate, newAmount: amt };
                    updateActiveScenario(function (s) { return { ...s, vaChanges: [...(s.vaChanges || []), entry] }; });
                    setScNewVaDate(""); setScNewVaAmount(""); setScShowAddVaChange(false);
                  }} style={{ width: "100%", padding: "6px 12px", borderRadius: 5, border: "none", background: T().btnBg, color: T().btnText, cursor: "pointer", fontSize: 11, fontFamily: "system-ui", fontWeight: 600 }}>Save Change</button>
                </div>
              )}
              {(activeScenario.vaChanges || []).length === 0 ? (
                <div style={{ fontSize: 10, color: T().textDim, fontStyle: "italic", padding: "2px 0" }}>No scheduled changes. Baseline VA of {fm(activeScenario.income ? (activeScenario.income.va || 0) : 0)} applies indefinitely.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {(activeScenario.vaChanges || []).slice().sort(function (a, b) { return a.effectiveDate.localeCompare(b.effectiveDate); }).map(function (c, idx) {
                    var d = new Date(c.effectiveDate + "T12:00:00");
                    var dateStr = (d.getMonth() + 1) + "/" + d.getDate() + "/" + d.getFullYear();
                    return (
                      <div key={c.id} style={{ background: idx % 2 === 0 ? rowEven : rowOdd, borderRadius: 5, padding: "6px 8px", display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ flex: 1, fontSize: 11, color: T().textPrimary }}>
                          Starting <span style={{ fontFamily: "monospace", color: T().accent }}>{dateStr}</span>: <span style={{ fontFamily: "monospace", color: T().green, fontWeight: 600 }}>{fm(c.newAmount)}</span>/month
                        </div>
                        {scConfirmDelVaChange === c.id ? (
                          <div style={{ display: "flex", gap: 3 }}>
                            <button onClick={function () { setScConfirmDelVaChange(null); }} style={{ background: "transparent", border: "1px solid " + T().inputBd, borderRadius: 4, color: T().textDim, fontSize: 9, padding: "1px 5px", cursor: "pointer", fontFamily: "system-ui" }}>No</button>
                            <button onClick={function () {
                              updateActiveScenario(function (s) { return { ...s, vaChanges: (s.vaChanges || []).filter(function (x) { return x.id !== c.id; }) }; });
                              setScConfirmDelVaChange(null);
                            }} style={{ background: T().red, border: "none", borderRadius: 4, color: "#fff", fontSize: 9, padding: "1px 5px", cursor: "pointer", fontFamily: "system-ui" }}>Yes</button>
                          </div>
                        ) : (
                          <button onClick={function () { setScConfirmDelVaChange(c.id); }} style={{ background: "none", border: "none", color: T().red, cursor: "pointer", fontSize: 13, padding: "0 4px" }}>×</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Time range selector */}
      <div style={{ display: "flex", gap: 4, marginBottom: 14, justifyContent: "center" }}>
        {[{ k: "2w", l: "2 Wk" }, { k: "1m", l: "1 Mo" }, { k: "3m", l: "3 Mo" }, { k: "6m", l: "6 Mo" }, { k: "1y", l: "1 Yr" }, { k: "2y", l: "2 Yr" }, { k: "3y", l: "3 Yr" }].map(function (r) {
          return <button key={r.k} onClick={function () { setViewRange(r.k); setStartWeek(PAST_WEEKS); }} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid " + (viewRange === r.k ? "rgba(74,143,231,0.35)" : "rgba(100,160,255,0.08)"), background: viewRange === r.k ? "rgba(74,143,231,0.12)" : "transparent", color: viewRange === r.k ? T().accent : T().textDim, cursor: "pointer", fontSize: 12, fontFamily: "system-ui", fontWeight: viewRange === r.k ? 600 : 400 }}>{r.l}</button>;
        })}
      </div>

      {/* Nav */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <button onClick={function () { setStartWeek(Math.max(0, startWeek - stepSize)); }} disabled={startWeek === 0} style={{ background: "none", border: "none", color: startWeek === 0 ? T().barTrack : T().accent, cursor: startWeek === 0 ? "default" : "pointer", padding: 8, fontSize: 18 }}>{"<"}</button>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: T().textSecondary }}>{visibleWeeks[0] ? fd(visibleWeeks[0][0].date) : ""} - {lastWk ? fd(lastWk[6].date) : ""}</div>
        </div>
        <button onClick={function () { setStartWeek(Math.min(TOTAL_WEEKS - totalWeeksNeeded, startWeek + stepSize)); }} disabled={startWeek >= TOTAL_WEEKS - totalWeeksNeeded} style={{ background: "none", border: "none", color: startWeek >= TOTAL_WEEKS - totalWeeksNeeded ? T().barTrack : T().accent, cursor: startWeek >= TOTAL_WEEKS - totalWeeksNeeded ? "default" : "pointer", padding: 8, fontSize: 18 }}>{">"}</button>
      </div>

      {/* Summary */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
        <div style={{ background: cardBg, borderRadius: 12, border: cardBd, padding: 12, textAlign: "center" }}>
          <div style={{ fontSize: 10, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>End Balance</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: T().textPrimary, fontFamily: "monospace", marginTop: 4 }}>{fm(endBal)}</div>
        </div>
        <div style={{ background: cardBg, borderRadius: 12, border: cardBd, padding: 12, textAlign: "center" }}>
          <div style={{ fontSize: 10, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>Period Change</div>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "monospace", marginTop: 4, color: change >= 0 ? T().green : T().red }}>{(change >= 0 ? "+" : "") + fm(change)}</div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <div style={{ flex: 1, background: "rgba(74,143,231,0.06)", borderRadius: 8, padding: "7px 12px", border: "1px solid rgba(74,143,231,0.12)", fontSize: 11, color: "#5080a8" }}>
          CC paydown cards show projected balances
        </div>
        <button onClick={function () { setShowAddTx(true); }} style={{ background: "rgba(92,184,138,0.1)", border: "1px solid rgba(92,184,138,0.25)", borderRadius: 8, padding: "7px 12px", color: T().green, cursor: "pointer", fontSize: 11, fontFamily: "system-ui", fontWeight: 600, whiteSpace: "nowrap" }}>+ One-time</button>
      </div>

      {/* Day headers */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
        {DAYS.map(function (d) { return <div key={d} style={{ textAlign: "center", fontSize: isDesktop ? 13 : 10, color: "#2e4260", fontWeight: 700, letterSpacing: "0.06em", padding: "4px 0" }}>{d}</div>; })}
      </div>

      {negativeDates.length > 0 && (
        <div onClick={function () {
          if (firstNegativeDate) {
            // Convert Date to YYYY-MM-DD for projDate. The existing useEffect
            // on projDate will jump the calendar to that week.
            var y = firstNegativeDate.getFullYear();
            var m = String(firstNegativeDate.getMonth() + 1).padStart(2, "0");
            var d = String(firstNegativeDate.getDate()).padStart(2, "0");
            ctx.setProjDate(y + "-" + m + "-" + d);
          }
        }} style={{ background: "rgba(196,92,92,0.12)", border: "1px solid " + T().red, borderRadius: 8, padding: "10px 14px", marginBottom: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: T().red, fontWeight: 700 }}>
              Bank balance goes negative on {firstNegativeDate ? fd(firstNegativeDate) : ""}
              {negativeDates.length > 1 ? " (and " + (negativeDates.length - 1) + " other day" + (negativeDates.length > 2 ? "s" : "") + ")" : ""}
            </div>
            <div style={{ fontSize: 10, color: T().textDim, marginTop: 2 }}>Tap to jump to the first negative week. Affected weeks are highlighted below.</div>
          </div>
        </div>
      )}

      {/* Scrollable calendar */}
      <div style={{ maxHeight: isDesktop ? 680 : 520, overflowY: "auto", borderRadius: 10, border: "1px solid rgba(100,160,255,0.06)", padding: 4 }}>
        {scrollableWeeks.map(function (wk, idx) {
          var prevWk = idx > 0 ? scrollableWeeks[idx - 1] : (startWeek > 0 ? weeks[startWeek - 1] : null);
          return renderWeek(wk, idx, prevWk, startWeek + idx);
        })}
      </div>

      {/* Activity list */}
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 11, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700, marginBottom: 8 }}>Activity This Period</div>
        <div style={{ maxHeight: 400, overflowY: "auto", borderRadius: 10, border: "1px solid rgba(100,160,255,0.06)", padding: 4 }}>
        {allTx.length === 0 && <div style={{ textAlign: "center", color: "#2e4260", fontSize: 12, padding: 20 }}>No scheduled activity</div>}
        {allTx.map(function (tx, i) {
          var typeLabel = tx.type === "income" ? "Income" : tx.type === "cc" ? "Credit Card" : tx.type === "onetime" ? "One-time" : "Auto-pay";
          var typeColor = tx.amount >= 0 ? T().green : tx.type === "cc" ? T().orange : tx.type === "onetime" ? "#7ab8d8" : T().red;
          var isZeroPaying = tx.zeroApr === true;
          var bgColor = isZeroPaying ? "rgba(230,200,80,0.08)" : tx.type === "onetime" ? "rgba(122,184,216,0.05)" : (i % 2 === 0 ? rowEven : rowOdd);
          var borderColor = isZeroPaying ? "1px solid rgba(230,200,80,0.2)" : tx.type === "onetime" ? "1px solid rgba(122,184,216,0.12)" : rowBd;
          return (
            <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: bgColor, borderRadius: 8, border: borderColor, marginBottom: 3 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: isZeroPaying ? T().yellow : T().textSecondary, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tx.note}{isZeroPaying ? " (0% APR)" : ""}</div>
                <div style={{ fontSize: 10, color: T().textDim, marginTop: 1 }}>{fd(tx.date)} | {typeLabel}</div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, fontFamily: "monospace", color: isZeroPaying ? T().yellow : typeColor, whiteSpace: "nowrap", marginLeft: 8 }}>{(tx.amount >= 0 ? "+" : "") + fm(tx.amount)}</div>
            </div>
          );
        })}
        </div>
      </div>

      {/* One-time transactions list */}
      {oneTimeTxs.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>One-Time Transactions</div>
            <div style={{ display: "flex", gap: 4 }}>
              <button onClick={function () { setOtFilter("upcoming"); }} style={{ padding: "3px 10px", borderRadius: 5, border: "1px solid " + (otFilter === "upcoming" ? "rgba(74,143,231,0.35)" : "rgba(100,160,255,0.08)"), background: otFilter === "upcoming" ? "rgba(74,143,231,0.12)" : "transparent", color: otFilter === "upcoming" ? T().accent : T().textDim, cursor: "pointer", fontSize: 10, fontFamily: "system-ui", fontWeight: otFilter === "upcoming" ? 600 : 400 }}>Upcoming</button>
              <button onClick={function () { setOtFilter("past"); }} style={{ padding: "3px 10px", borderRadius: 5, border: "1px solid " + (otFilter === "past" ? "rgba(74,143,231,0.35)" : "rgba(100,160,255,0.08)"), background: otFilter === "past" ? "rgba(74,143,231,0.12)" : "transparent", color: otFilter === "past" ? T().accent : T().textDim, cursor: "pointer", fontSize: 10, fontFamily: "system-ui", fontWeight: otFilter === "past" ? 600 : 400 }}>Past</button>
              <button onClick={function () { setOtFilter("all"); }} style={{ padding: "3px 10px", borderRadius: 5, border: "1px solid " + (otFilter === "all" ? "rgba(74,143,231,0.35)" : "rgba(100,160,255,0.08)"), background: otFilter === "all" ? "rgba(74,143,231,0.12)" : "transparent", color: otFilter === "all" ? T().accent : T().textDim, cursor: "pointer", fontSize: 10, fontFamily: "system-ui", fontWeight: otFilter === "all" ? 600 : 400 }}>All</button>
            </div>
          </div>
          {filteredOtTxs.map(function (tx, idx) {
            var d = new Date(tx.date + "T12:00:00");
            var cardName = "";
            if (tx.cardId) { cards.forEach(function (c) { if (String(c.id) === String(tx.cardId)) cardName = c.name; }); }
            return (
              <div key={tx.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: idx % 2 === 0 ? rowEven : rowOdd, borderRadius: 8, border: "1px solid rgba(122,184,216,0.1)", marginBottom: 4 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: T().textSecondary, fontWeight: 500 }}>{tx.note}</div>
                  <div style={{ fontSize: 10, color: T().textDim, marginTop: 1 }}>{fd(d)} | {tx.amount >= 0 ? "Deposit" : "Withdrawal"}{cardName ? " | " : ""}{cardName && <span style={{ color: T().orange }}>{cardName}</span>}</div>
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, fontFamily: "monospace", color: tx.amount >= 0 ? T().green : T().red, marginRight: 10 }}>{(tx.amount >= 0 ? "+" : "") + fm(tx.amount)}</div>
                <button onClick={function () {
                  // Per option B: txs are pure records; c.balance is never auto-modified.
                  // Removing a tx just removes the record — no balance restoration needed.
                  setOneTimeTxs(function (prev) { return prev.filter(function (x) { return x.id !== tx.id; }); });
                }} style={{ background: "none", border: "none", color: T().red, cursor: "pointer", fontSize: 14, padding: "2px 6px" }}>x</button>
              </div>
            );
          })}
        </div>
      )}

      {/* Add one-time transaction modal */}
      {showAddTx && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }} onClick={function () { setShowAddTx(false); }}>
          <div style={{ background: T().cardBg, borderRadius: 14, padding: 20, width: "100%", maxWidth: 340, border: "1px solid " + T().inputBd }} onClick={function (e) { e.stopPropagation(); }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: T().textSecondary, marginBottom: 14 }}>Add One-Time Transaction</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <button onClick={function () { setTxIsDeposit(true); setTxCardId(""); }} style={{ flex: 1, padding: "8px", borderRadius: 6, border: "1px solid " + (txIsDeposit ? "rgba(92,184,138,0.4)" : "rgba(100,160,255,0.1)"), background: txIsDeposit ? "rgba(92,184,138,0.1)" : "transparent", color: txIsDeposit ? T().green : T().textDim, cursor: "pointer", fontSize: 13, fontFamily: "system-ui", fontWeight: 500 }}>Deposit</button>
              <button onClick={function () { setTxIsDeposit(false); }} style={{ flex: 1, padding: "8px", borderRadius: 6, border: "1px solid " + (!txIsDeposit ? "rgba(196,92,92,0.4)" : "rgba(100,160,255,0.1)"), background: !txIsDeposit ? "rgba(196,92,92,0.1)" : "transparent", color: !txIsDeposit ? T().red : T().textDim, cursor: "pointer", fontSize: 13, fontFamily: "system-ui", fontWeight: 500 }}>Withdrawal</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div>
                <div style={{ fontSize: 11, color: T().textMuted, marginBottom: 3 }}>Date</div>
                <input type="date" value={txDate} onChange={function (e) { setTxDate(e.target.value); }} style={{ width: "100%", padding: "8px", background: T().inputBg, border: "1px solid " + T().inputBd, borderRadius: 6, color: T().textPrimary, fontSize: 13, fontFamily: "monospace", outline: "none", boxSizing: "border-box" }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: T().textMuted, marginBottom: 3 }}>Amount</div>
                <input placeholder="0" value={txAmt} onChange={function (e) { setTxAmt(e.target.value); }} style={{ width: "100%", padding: "8px", background: T().inputBg, border: "1px solid " + T().inputBd, borderRadius: 6, color: T().textPrimary, fontSize: 13, fontFamily: "monospace", outline: "none", boxSizing: "border-box", textAlign: "right" }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: T().textMuted, marginBottom: 3 }}>Description</div>
                <input placeholder="e.g. Tax refund, Motorcycle payoff" value={txNote} onChange={function (e) { setTxNote(e.target.value); }} style={{ width: "100%", padding: "8px", background: T().inputBg, border: "1px solid " + T().inputBd, borderRadius: 6, color: T().textPrimary, fontSize: 13, fontFamily: "system-ui", outline: "none", boxSizing: "border-box" }} />
              </div>
              {!txIsDeposit && (
                <div>
                  <div style={{ fontSize: 11, color: T().textMuted, marginBottom: 3 }}>Apply to Credit Card (optional)</div>
                  <select value={txCardId} onChange={function (e) { setTxCardId(e.target.value); }} style={{ width: "100%", padding: "8px", background: T().inputBg, border: "1px solid " + T().inputBd, borderRadius: 6, color: T().textPrimary, fontSize: 13, fontFamily: "system-ui", outline: "none", boxSizing: "border-box", cursor: "pointer" }}>
                    <option value="">None - regular withdrawal</option>
                    {(function () {
                      // Show the card balance projected to txDate so the user can see
                      // the actual payoff amount at that date — accounting for monthly
                      // interest accrual, simulated minimum payments, and any other
                      // already-scheduled card payments between today and txDate.
                      // Falls back to current balance if no date is picked or the date
                      // is today/past.
                      var projAtDate = null;
                      if (txDate) {
                        try { projAtDate = projectCardBalances(cards, txDate, oneTimeTxs); } catch (e) { projAtDate = null; }
                      }
                      var dateLabel = "";
                      if (txDate && projAtDate) {
                        var d = new Date(txDate + "T12:00:00");
                        dateLabel = " on " + (d.getMonth() + 1) + "/" + d.getDate();
                      }
                      return cards.map(function (c) {
                        var displayBal = (projAtDate && projAtDate[c.id] !== undefined) ? projAtDate[c.id] : c.balance;
                        // Filter: skip cards that have zero balance both today AND at the
                        // projected date — they don't need a payoff.
                        if (c.balance <= 0 && displayBal <= 0) return null;
                        return <option key={c.id} value={c.id}>{c.name} (bal{dateLabel}: {fm(displayBal)})</option>;
                      });
                    })()}
                  </select>
                  {txCardId && <div style={{ fontSize: 10, color: T().orange, marginTop: 4 }}>This will deduct from the card balance and skip any regular payment within 30 days.</div>}
                </div>
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                <button onClick={function () { setShowAddTx(false); setTxCardId(""); }} style={{ flex: 1, padding: "10px", borderRadius: 6, border: "1px solid rgba(100,160,255,0.1)", background: "transparent", color: T().textDim, cursor: "pointer", fontSize: 13, fontFamily: "system-ui" }}>Cancel</button>
                <button onClick={function () {
                  var amt = parseFloat(txAmt);
                  if (!amt || !txNote || !txDate) return;
                  var finalAmt = txIsDeposit ? Math.abs(amt) : -Math.abs(amt);
                  var cardId = txCardId ? parseInt(txCardId) : null;
                  var newTx = { id: Date.now(), date: txDate, amount: finalAmt, note: txNote, cardId: cardId };
                  setOneTimeTxs(function (prev) { return [...prev, newTx]; });
                  // Per option B: card-linked one-time txs are pure records.
                  // c.balance is the user-maintained "as of right now" baseline and
                  // is never auto-modified by tx creation. Date-aware views compute
                  // effective balances by combining c.balance with txs in the window.
                  setTxAmt(""); setTxNote(""); setTxCardId(""); setShowAddTx(false);
                }} style={{ flex: 1, padding: "10px", borderRadius: 6, border: "none", background: T().btnBg, color: T().btnText, cursor: "pointer", fontSize: 13, fontFamily: "system-ui", fontWeight: 600 }}>Add</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* DEBT PAYOFF TAB */
function DebtTab() {
  var ctx = useBudget();
  var cards = ctx.cards;
  var oneTimeTxs = ctx.oneTimeTxs;
  var projDate = ctx.projDate, setProjDate = ctx.setProjDate;
  const [strategy, setStrategy] = useState("avalanche");
  const [extraPayment, setExtraPayment] = useState(0);
  var projectedBals = projDate ? projectCardBalances(cards, projDate, oneTimeTxs) : null;
  var displayCards = projectedBals ? cards.map(function (c) {
    return { ...c, balance: projectedBals[c.id] !== undefined ? projectedBals[c.id] : c.balance };
  }) : cards.slice();
  displayCards.sort(function (a, b) { return a.dueDay - b.dueDay; });

  // Only include cards with balances
  var debtCards = displayCards.filter(function (c) { return c.balance > 0; });
  var totalDebt = debtCards.reduce(function (s, c) { return s + c.balance; }, 0);
  var totalMinPayments = debtCards.reduce(function (s, c) { return s + estPayment(c.balance, c.apr, c.minPct, c.zero); }, 0);

  // Simulate payoff for a given strategy
  function simulatePayoff(strat, extra) {
    var cardSims = debtCards.map(function (c) {
      return { id: c.id, name: c.name, balance: c.balance, startBalance: c.balance, apr: c.apr, zero: c.zero, minPct: c.minPct, payment: estPayment(c.balance, c.apr, c.minPct, c.zero), totalPaid: 0, totalInterest: 0, paidOffMonth: null };
    });

    // Sort by strategy
    if (strat === "avalanche") {
      cardSims.sort(function (a, b) { return b.apr - a.apr; });
    } else if (strat === "snowball") {
      cardSims.sort(function (a, b) { return a.balance - b.balance; });
    } else if (strat === "highbalance") {
      cardSims.sort(function (a, b) { return b.balance - a.balance; });
    }

    var months = 0;
    var maxMonths = 360; // 30 years cap
    var totalInterestPaid = 0;
    var totalAmountPaid = 0;
    var monthlySnapshots = [];

    while (months < maxMonths) {
      var allPaidOff = true;
      var monthInterest = 0;
      var monthPaid = 0;

      // Apply interest
      for (var i = 0; i < cardSims.length; i++) {
        var c = cardSims[i];
        if (c.balance <= 0) continue;
        allPaidOff = false;
        if (c.apr > 0 && !c.zero) {
          var interest = c.balance * (c.apr / 100 / 12);
          c.balance += interest;
          c.totalInterest += interest;
          monthInterest += interest;
        }
      }

      if (allPaidOff) break;

      // Calculate total freed-up money from already paid-off cards + extra payment
      var freedUp = extra;
      for (var j = 0; j < cardSims.length; j++) {
        var c2 = cardSims[j];
        if (c2.balance <= 0 && c2.paidOffMonth !== null) {
          // This card was paid off previously — its original min payment is now available
          freedUp += c2.payment;
        }
      }

      // Apply minimum payments to active cards
      for (var k = 0; k < cardSims.length; k++) {
        var c3 = cardSims[k];
        if (c3.balance <= 0) continue;
        var minPmt = estPayment(c3.balance, c3.apr, c3.minPct, c3.zero);
        var pmt = Math.min(minPmt, c3.balance);
        c3.balance -= pmt;
        c3.totalPaid += pmt;
        monthPaid += pmt;
        if (c3.balance <= 0.01) {
          c3.balance = 0;
          if (c3.paidOffMonth === null) c3.paidOffMonth = months + 1;
        }
      }

      // Apply freed-up money to the target card (first unpaid in strategy order)
      for (var m = 0; m < cardSims.length; m++) {
        var c4 = cardSims[m];
        if (c4.balance <= 0 || freedUp <= 0) continue;
        var extraPmt = Math.min(freedUp, c4.balance);
        c4.balance -= extraPmt;
        c4.totalPaid += extraPmt;
        monthPaid += extraPmt;
        freedUp -= extraPmt;
        if (c4.balance <= 0.01) {
          c4.balance = 0;
          if (c4.paidOffMonth === null) c4.paidOffMonth = months + 1;
        }
        break; // Only apply to the first target card in strategy order
      }

      totalInterestPaid += monthInterest;
      totalAmountPaid += monthPaid;
      months++;

      if (months % 3 === 0 || months <= 6) {
        monthlySnapshots.push({
          month: months,
          remaining: cardSims.reduce(function (s, c) { return s + c.balance; }, 0),
          interestSoFar: totalInterestPaid
        });
      }
    }

    return {
      months: months,
      totalInterest: totalInterestPaid,
      totalPaid: totalAmountPaid,
      cards: cardSims,
      snapshots: monthlySnapshots
    };
  }

  var result = useMemo(function () {
    return simulatePayoff(strategy, extraPayment);
  }, [cards, projDate, strategy, extraPayment, oneTimeTxs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Also compute baseline (minimum payments only, no strategy)
  var baseline = useMemo(function () {
    return simulatePayoff("avalanche", 0);
  }, [cards, projDate, oneTimeTxs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compare all strategies
  var strategies = [
    { key: "avalanche", name: "Avalanche", desc: "Highest APR first — saves the most interest" },
    { key: "snowball", name: "Snowball", desc: "Smallest balance first — fastest wins for motivation" },
    { key: "highbalance", name: "High Balance", desc: "Largest balance first — tackles big debts early" },
  ];
  var stratResults = useMemo(function () {
    return strategies.map(function (s) {
      var r = simulatePayoff(s.key, extraPayment);
      return { ...s, months: r.months, totalInterest: r.totalInterest, totalPaid: r.totalPaid };
    });
  }, [cards, projDate, extraPayment, oneTimeTxs]); // eslint-disable-line react-hooks/exhaustive-deps

  var interestSaved = baseline.totalInterest - result.totalInterest;
  var monthsSaved = baseline.months - result.months;

  var payoffYear = function (months) {
    var d = new Date();
    d.setMonth(d.getMonth() + months);
    return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  };

  return (
    <div style={{ padding: "0 12px 130px" }}>
      {/* Projection date picker */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: T().headerColor, fontWeight: 500, whiteSpace: "nowrap" }}>Viewing:</div>
        <button onClick={function () { setProjDate(""); }} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid " + (!projDate ? "rgba(74,143,231,0.35)" : "rgba(100,160,255,0.08)"), background: !projDate ? "rgba(74,143,231,0.12)" : "transparent", color: !projDate ? T().accent : T().textDim, cursor: "pointer", fontSize: 12, fontFamily: "system-ui", fontWeight: !projDate ? 600 : 400 }}>Today</button>
        <input type="date" value={projDate} onChange={function (e) { setProjDate(e.target.value); }} style={{ padding: "6px 10px", background: T().inputBg, border: "1px solid " + T().inputBd, borderRadius: 6, color: T().textPrimary, fontSize: 12, fontFamily: "monospace", outline: "none", boxSizing: "border-box" }} />
        {projDate && <div style={{ fontSize: 10, color: T().orange }}>Projected balances for paydown cards</div>}
      </div>

      {/* Overview */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
        <div style={{ background: cardBg, borderRadius: 12, border: cardBd, padding: "12px 8px", textAlign: "center" }}>
          <div style={{ fontSize: 10, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>Total Debt</div>
          <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "monospace", marginTop: 4, color: T().red }}>{fm(totalDebt)}</div>
          <div style={{ fontSize: 9, color: T().textDim, marginTop: 2 }}>{debtCards.length} cards</div>
        </div>
        <div style={{ background: cardBg, borderRadius: 12, border: cardBd, padding: "12px 8px", textAlign: "center" }}>
          <div style={{ fontSize: 10, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>Debt Free By</div>
          <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "monospace", marginTop: 4, color: T().green }}>{payoffYear(result.months)}</div>
          <div style={{ fontSize: 9, color: T().textDim, marginTop: 2 }}>{result.months} months</div>
        </div>
        <div style={{ background: cardBg, borderRadius: 12, border: cardBd, padding: "12px 8px", textAlign: "center" }}>
          <div style={{ fontSize: 10, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>Total Interest</div>
          <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "monospace", marginTop: 4, color: T().orange }}>{fm(result.totalInterest)}</div>
          <div style={{ fontSize: 9, color: T().textDim, marginTop: 2 }}>over payoff period</div>
        </div>
      </div>

      {/* Interest saved banner */}
      {(extraPayment > 0 || strategy !== "avalanche") && interestSaved > 0 && (
        <div style={{ background: "rgba(92,184,138,0.08)", borderRadius: 10, border: "1px solid rgba(92,184,138,0.2)", padding: "12px 14px", marginBottom: 14, textAlign: "center" }}>
          <div style={{ fontSize: 13, color: T().green, fontWeight: 600 }}>You save {fm(interestSaved)} in interest{monthsSaved > 0 ? " and pay off " + monthsSaved + " months sooner" : ""}</div>
          <div style={{ fontSize: 10, color: T().textDim, marginTop: 2 }}>compared to minimum payments only</div>
        </div>
      )}

      {/* Strategy selector */}
      <div style={{ background: cardBg, borderRadius: 12, border: cardBd, padding: 14, marginBottom: 12 }}>
        <div style={{ fontSize: 10, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, marginBottom: 10 }}>Payoff Strategy</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
          {strategies.map(function (s) {
            var isActive = strategy === s.key;
            return (
              <button key={s.key} onClick={function () { setStrategy(s.key); }} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "10px 14px", borderRadius: 8,
                border: "1px solid " + (isActive ? "rgba(74,143,231,0.35)" : "rgba(100,160,255,0.08)"),
                background: isActive ? "rgba(74,143,231,0.1)" : "transparent",
                cursor: "pointer", textAlign: "left"
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: isActive ? T().accent : "#7a9ab8", fontFamily: "system-ui" }}>{s.name}</div>
                  <div style={{ fontSize: 10, color: T().textDim, marginTop: 2, fontFamily: "system-ui" }}>{s.desc}</div>
                </div>
                <div style={{ textAlign: "right", marginLeft: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: isActive ? T().accent : "#5a7a9a", fontFamily: "monospace" }}>{stratResults.find(function (r) { return r.key === s.key; }).months} mo</div>
                </div>
              </button>
            );
          })}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 12, color: T().headerColor, fontWeight: 500, whiteSpace: "nowrap" }}>Extra monthly payment</div>
          <NumInput value={extraPayment} onChange={function (v) { setExtraPayment(Math.max(0, v)); }} style={{ width: 90, padding: "7px 8px", background: T().inputBg, border: "1px solid " + T().inputBd, borderRadius: 6, color: T().green, fontSize: 14, fontFamily: "monospace", outline: "none", textAlign: "right", boxSizing: "border-box" }} />
        </div>
      </div>

      {/* Strategy comparison */}
      <div style={{ background: cardBg, borderRadius: 12, border: cardBd, overflow: "hidden", marginBottom: 12 }}>
        <div style={{ padding: "12px 14px 8px" }}>
          <span style={{ fontSize: 11, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>Strategy Comparison</span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 380 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(100,160,255,0.08)" }}>
                <th style={{ ...hs, textAlign: "left", paddingLeft: 14 }}>Strategy</th>
                <th style={hs}>Months</th>
                <th style={hs}>Total Interest</th>
                <th style={{ ...hs, paddingRight: 14 }}>Total Paid</th>
              </tr>
            </thead>
            <tbody>
              {stratResults.map(function (s, idx) {
                var isActive = strategy === s.key;
                return (
                  <tr key={s.key} style={{ borderBottom: rowBd, background: isActive ? "rgba(74,143,231,0.06)" : idx % 2 === 0 ? rowEven : rowOdd }}>
                    <td style={{ ...ns, paddingLeft: 14, color: isActive ? T().accent : "#7a9ab8", fontWeight: isActive ? 700 : 500 }}>{s.name}</td>
                    <td style={{ ...cs, color: isActive ? T().accent : T().textPrimary }}>{s.months}</td>
                    <td style={{ ...cs, color: T().orange }}>{fm(s.totalInterest)}</td>
                    <td style={{ ...cs, paddingRight: 14 }}>{fm(s.totalPaid)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recommended payoff order */}
      <div style={{ background: cardBg, borderRadius: 12, border: cardBd, overflow: "hidden", marginBottom: 12 }}>
        <div style={{ padding: "12px 14px 8px" }}>
          <span style={{ fontSize: 11, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>Payoff Order ({strategy === "avalanche" ? "Highest APR First" : strategy === "snowball" ? "Smallest Balance First" : "Largest Balance First"})</span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 460 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(100,160,255,0.08)" }}>
                <th style={{ ...hs, textAlign: "left", paddingLeft: 14, width: 30 }}>#</th>
                <th style={{ ...hs, textAlign: "left" }}>Card</th>
                <th style={hs}>Balance</th>
                <th style={hs}>Used</th>
                <th style={hs}>APR</th>
                <th style={hs}>Min Pmt</th>
                <th style={hs}>Interest</th>
                <th style={{ ...hs, paddingRight: 14 }}>Paid Off</th>
              </tr>
            </thead>
            <tbody>
              {result.cards.map(function (c, idx) {
                var actual = cards.find(function (x) { return x.id === c.id; });
                var u = actual && actual.limit > 0 ? c.startBalance / actual.limit : 0;
                return (
                  <tr key={c.id} style={{ borderBottom: rowBd, background: utilRowBg(u) }}>
                    <td style={{ padding: "8px 4px 8px 14px", fontSize: 14, fontWeight: 700, color: T().accent, fontFamily: "monospace" }}>{idx + 1}</td>
                    <td style={{ ...ns, fontWeight: 600 }}>{c.name}</td>
                    <td style={{ ...cs, color: T().red }}>{fm(c.startBalance)}</td>
                    <td style={{ ...cs, color: utilColor(u), fontWeight: 600 }}>{actual && actual.limit > 0 ? fp(u) : "—"}</td>
                    <td style={{ ...cs, color: c.apr > 0 ? T().orange : T().green }}>{c.zero ? "0%" : c.apr + "%"}</td>
                    <td style={cs}>{fm(c.payment)}</td>
                    <td style={{ ...cs, color: T().orange }}>{fm(c.totalInterest)}</td>
                    <td style={{ ...cs, paddingRight: 14, color: T().green, fontWeight: 600 }}>{c.paidOffMonth ? payoffYear(c.paidOffMonth) : "N/A"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Interest breakdown */}
      <div style={{ background: cardBg, borderRadius: 12, border: cardBd, padding: 14 }}>
        <div style={{ fontSize: 10, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, marginBottom: 10 }}>Interest Breakdown by Card</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {result.cards.filter(function (c) { return c.totalInterest > 0; }).sort(function (a, b) { return b.totalInterest - a.totalInterest; }).map(function (c, idx) {
            var pct = result.totalInterest > 0 ? (c.totalInterest / result.totalInterest) * 100 : 0;
            var actual = cards.find(function (x) { return x.id === c.id; });
            var u = actual && actual.limit > 0 ? c.startBalance / actual.limit : 0;
            return (
              <div key={c.id} style={{ background: utilRowBg(u), borderRadius: 8, padding: "10px 12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <span style={{ fontSize: 13, color: T().textSecondary, fontWeight: 500 }}>{c.name}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T().orange, fontFamily: "monospace" }}>{fm(c.totalInterest)}</span>
                </div>
                <div style={{ height: 4, background: T().barTrack, borderRadius: 2, overflow: "hidden" }}>
                  <div style={{ height: "100%", borderRadius: 2, background: T().orange, width: pct + "%", transition: "width 0.3s" }} />
                </div>
                <div style={{ fontSize: 9, color: T().textDim, marginTop: 2 }}>{pct.toFixed(1)}% of total interest</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* SETTINGS TAB */
function SettingsTab() {
  var ctx = useBudget();
  var cards = ctx.cards, setCards = ctx.setCards;
  var payroll = ctx.payroll, setPayroll = ctx.setPayroll;
  var income = ctx.income, setIncome = ctx.setIncome;
  var bills = ctx.bills, setBills = ctx.setBills;
  var startingBalance = ctx.startingBalance, setStartingBalance = ctx.setStartingBalance;
  var savingsBalance = ctx.savingsBalance, setSavingsBalance = ctx.setSavingsBalance;
  var vaChanges = ctx.vaChanges, setVaChanges = ctx.setVaChanges;
  var salaryChanges = ctx.salaryChanges, setSalaryChanges = ctx.setSalaryChanges;
  var oneTimeTxs = ctx.oneTimeTxs, setOneTimeTxs = ctx.setOneTimeTxs;
  var scenarios = ctx.scenarios || [];
  var setScenarios = ctx.setScenarios;
  var setActiveScenarioId = ctx.setActiveScenarioId;
  var cardTargetPct = ctx.cardTargetPct;
  var setCardTargetPct = ctx.setCardTargetPct;
  var currentUser = ctx.currentUser;
  var themeMode = ctx.themeMode, setThemeMode = ctx.setThemeMode;
  var inp = { width: 60, padding: "5px 6px", background: T().inputBg, border: "1px solid " + T().inputBd, borderRadius: 5, color: T().textPrimary, fontSize: 12, fontFamily: "monospace", outline: "none", textAlign: "right", boxSizing: "border-box" };
  var selStyle = { padding: "7px 8px", background: T().inputBg, border: "1px solid " + T().inputBd, borderRadius: 6, color: T().textPrimary, fontSize: 13, fontFamily: "system-ui", outline: "none", width: "100%", boxSizing: "border-box", cursor: "pointer" };
  var dateInp = { padding: "7px 8px", background: T().inputBg, border: "1px solid " + T().inputBd, borderRadius: 6, color: T().textPrimary, fontSize: 13, fontFamily: "monospace", outline: "none", width: "100%", boxSizing: "border-box" };
  var labelStyle = { fontSize: 12, color: T().headerColor, fontWeight: 500, marginBottom: 4 };
  var addInp = { padding: "7px 8px", background: T().inputBg, border: "1px solid " + T().inputBd, borderRadius: 6, color: T().textPrimary, fontSize: 12, fontFamily: "monospace", outline: "none", boxSizing: "border-box" };

  const [showAddBill, setShowAddBill] = useState(false);
  const [nbName, setNbName] = useState("");
  const [nbAmt, setNbAmt] = useState("");
  const [nbDay, setNbDay] = useState("");
  const [confirmDelBill, setConfirmDelBill] = useState(null);

  const [showAddCard, setShowAddCard] = useState(false);
  const [ncName, setNcName] = useState("");
  const [ncBal, setNcBal] = useState("");
  const [ncLim, setNcLim] = useState("");
  const [ncApr, setNcApr] = useState("");
  const [ncDay, setNcDay] = useState("");
  const [confirmDelCard, setConfirmDelCard] = useState(null);
  const [showAddVaChange, setShowAddVaChange] = useState(false);
  const [newVaDate, setNewVaDate] = useState("");
  const [newVaAmount, setNewVaAmount] = useState("");
  const [confirmDelVaChange, setConfirmDelVaChange] = useState(null);
  const [showAddSalaryChange, setShowAddSalaryChange] = useState(false);
  const [newSalaryDate, setNewSalaryDate] = useState("");
  const [newSalaryAmount, setNewSalaryAmount] = useState("");
  const [confirmDelSalaryChange, setConfirmDelSalaryChange] = useState(null);
  const [confirmRefresh, setConfirmRefresh] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [restorePayload, setRestorePayload] = useState(null);
  const [restoreError, setRestoreError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  // User management state
  const [showPinChange, setShowPinChange] = useState(false);
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [pinMsg, setPinMsg] = useState("");
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newUserPin, setNewUserPin] = useState("");
  const [newUserConfirm, setNewUserConfirm] = useState("");
  const [userMsg, setUserMsg] = useState("");
  const [users, setUsers] = useState([]);
  const [confirmDelUser, setConfirmDelUser] = useState(null);

  // Load users list on mount (admin only)
  useEffect(function () {
    if (currentUser && currentUser.is_admin) {
      listUsers().then(function (u) { setUsers(u); });
    }
  }, [currentUser]);

  var freqLabel = payroll.frequency === "weekly" ? "Every week from first pay date" : payroll.frequency === "biweekly" ? "Every 2 weeks from first pay date" : payroll.frequency === "bimonthly" ? "Twice a month (1st pay date + 15 days later)" : "Once a month on the pay date";
  var payPerCheck = payroll.frequency === "monthly" ? "Full monthly salary" : payroll.frequency === "weekly" ? "1/4 of monthly salary" : "Half of monthly salary";

  // Compute next few pay dates for preview
  var currentYear = new Date().getFullYear();
  var previewStart = new Date(currentYear, new Date().getMonth(), 1);
  var previewEnd = new Date(currentYear, new Date().getMonth() + 3, 0);
  var previewDates = getPayDates(payroll, previewStart, previewEnd);

  // Compute which months in the current year have 3 paychecks (bi-weekly only)
  var yearStart = new Date(currentYear, 0, 1);
  var yearEnd = new Date(currentYear, 11, 31);
  var yearPayDates = getPayDates(payroll, yearStart, yearEnd);
  var monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  var paysByMonth = {};
  yearPayDates.forEach(function (d) {
    var m = d.getMonth();
    if (!paysByMonth[m]) paysByMonth[m] = [];
    paysByMonth[m].push(d);
  });
  var tripleMonths = [];
  Object.keys(paysByMonth).forEach(function (m) {
    if (paysByMonth[m].length >= 3) {
      tripleMonths.push({ month: parseInt(m), dates: paysByMonth[m] });
    }
  });

  return (
    <div style={{ padding: "0 12px 130px" }}>
      {/* Starting Balance */}
      <div style={{ background: cardBg, borderRadius: 12, border: cardBd, overflow: "hidden", marginBottom: 12 }}>
        <div style={{ padding: "12px 14px 8px" }}>
          <span style={{ fontSize: 11, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>Bank Balance</span>
          <div style={{ fontSize: 10, color: T().textDim, marginTop: 4 }}>Set your current checking and savings balances. The calendar uses checking as the starting point for projections; savings is shown for reference and stays static across the projection.</div>
        </div>
        <div style={{ padding: "4px 14px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ fontSize: 13, color: T().headerColor, fontWeight: 500 }}>Checking Balance</div>
            <div style={{ flex: 1 }}></div>
            <NumInput value={startingBalance} onChange={function (v) { setStartingBalance(v); }} style={{ ...inp, width: 120, fontSize: 16, padding: "8px 10px" }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ fontSize: 13, color: T().headerColor, fontWeight: 500 }}>Savings Balance</div>
            <div style={{ flex: 1 }}></div>
            <NumInput value={savingsBalance} onChange={function (v) { setSavingsBalance(v); }} style={{ ...inp, width: 120, fontSize: 16, padding: "8px 10px" }} />
          </div>
        </div>
      </div>

      {/* VA Scheduled Changes — lets the user record future VA disability rate
          changes (e.g. "rating bumps to X effective Nov 1"). The calendar's
          monthly VA payment uses effectiveVA() to pick the right amount on
          each date. Only future-dated changes are accepted. */}
      <div style={{ background: cardBg, borderRadius: 12, border: cardBd, overflow: "hidden", marginBottom: 12 }}>
        <div style={{ padding: "12px 14px 8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 11, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>VA Scheduled Changes</span>
          <button onClick={function () { setShowAddVaChange(!showAddVaChange); setNewVaDate(""); setNewVaAmount(""); }} style={{ background: "rgba(74,143,231,0.1)", border: "1px solid rgba(74,143,231,0.25)", borderRadius: 4, color: T().accent, fontSize: 10, padding: "3px 8px", cursor: "pointer", fontFamily: "system-ui", fontWeight: 600 }}>{showAddVaChange ? "Cancel" : "+ Add"}</button>
        </div>
        <div style={{ padding: "4px 14px 14px" }}>
          <div style={{ fontSize: 11, color: T().textDim, marginBottom: 10, lineHeight: 1.4 }}>
            Record future VA payment increases. The calendar will switch to the new amount on the effective date. Baseline VA (used until any scheduled change) is set on the Bills tab.
          </div>
          {showAddVaChange && (
            <div style={{ background: T().inputBg, borderRadius: 8, padding: 12, marginBottom: 10, border: "1px solid " + T().inputBd }}>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, color: T().textMuted, marginBottom: 3 }}>Effective Date</div>
                  <input type="date" min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split("T")[0]} value={newVaDate} onChange={function (e) { setNewVaDate(e.target.value); }} style={{ ...dateInp, fontSize: 12, padding: "6px 8px" }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, color: T().textMuted, marginBottom: 3 }}>New Amount</div>
                  <input type="number" placeholder="0" value={newVaAmount} onChange={function (e) { setNewVaAmount(e.target.value); }} style={{ ...addInp, width: "100%", fontSize: 12, padding: "6px 8px", textAlign: "right" }} />
                </div>
              </div>
              <button onClick={function () {
                var amt = parseFloat(newVaAmount);
                if (!newVaDate || !amt || amt < 0) return;
                // Reject past or today's date defensively
                var today = new Date(); today.setHours(0, 0, 0, 0);
                var ed = new Date(newVaDate + "T12:00:00");
                if (ed <= today) return;
                var entry = { id: Date.now(), effectiveDate: newVaDate, newAmount: amt };
                setVaChanges(function (prev) { return [...prev, entry]; });
                setNewVaDate(""); setNewVaAmount(""); setShowAddVaChange(false);
              }} style={{ width: "100%", padding: "7px 14px", borderRadius: 6, border: "none", background: T().btnBg, color: T().btnText, cursor: "pointer", fontSize: 12, fontFamily: "system-ui", fontWeight: 600 }}>Save Change</button>
            </div>
          )}
          {vaChanges.length === 0 ? (
            <div style={{ fontSize: 11, color: T().textDim, fontStyle: "italic", padding: "6px 0" }}>No scheduled changes. Baseline VA of {fm(income.va.current)} applies indefinitely.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {vaChanges.slice().sort(function (a, b) { return a.effectiveDate.localeCompare(b.effectiveDate); }).map(function (c, idx) {
                var d = new Date(c.effectiveDate + "T12:00:00");
                var dateStr = (d.getMonth() + 1) + "/" + d.getDate() + "/" + d.getFullYear();
                return (
                  <div key={c.id} style={{ background: idx % 2 === 0 ? rowEven : rowOdd, borderRadius: 6, padding: "8px 10px", display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ flex: 1, fontSize: 12, color: T().textPrimary }}>
                      Starting <span style={{ fontFamily: "monospace", color: T().accent }}>{dateStr}</span>: <span style={{ fontFamily: "monospace", color: T().green, fontWeight: 600 }}>{fm(c.newAmount)}</span>/month
                    </div>
                    {confirmDelVaChange === c.id ? (
                      <div style={{ display: "flex", gap: 4 }}>
                        <button onClick={function () { setConfirmDelVaChange(null); }} style={{ background: "transparent", border: "1px solid " + T().inputBd, borderRadius: 4, color: T().textDim, fontSize: 9, padding: "2px 6px", cursor: "pointer", fontFamily: "system-ui" }}>No</button>
                        <button onClick={function () { setVaChanges(function (prev) { return prev.filter(function (x) { return x.id !== c.id; }); }); setConfirmDelVaChange(null); }} style={{ background: T().red, border: "none", borderRadius: 4, color: "#fff", fontSize: 9, padding: "2px 6px", cursor: "pointer", fontFamily: "system-ui" }}>Yes</button>
                      </div>
                    ) : (
                      <button onClick={function () { setConfirmDelVaChange(c.id); }} style={{ background: "none", border: "none", color: T().red, cursor: "pointer", fontSize: 14, padding: "0 4px" }}>×</button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Salary Scheduled Changes — same pattern as VA changes but for monthly
          salary. The calendar's per-paycheck amount is derived from the
          effective monthly salary on each pay date, so a scheduled change
          flows through to all paychecks on or after the effective date. */}
      <div style={{ background: cardBg, borderRadius: 12, border: cardBd, overflow: "hidden", marginBottom: 12 }}>
        <div style={{ padding: "12px 14px 8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 11, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>Salary Scheduled Changes</span>
          <button onClick={function () { setShowAddSalaryChange(!showAddSalaryChange); setNewSalaryDate(""); setNewSalaryAmount(""); }} style={{ background: "rgba(74,143,231,0.1)", border: "1px solid rgba(74,143,231,0.25)", borderRadius: 4, color: T().accent, fontSize: 10, padding: "3px 8px", cursor: "pointer", fontFamily: "system-ui", fontWeight: 600 }}>{showAddSalaryChange ? "Cancel" : "+ Add"}</button>
        </div>
        <div style={{ padding: "4px 14px 14px" }}>
          <div style={{ fontSize: 11, color: T().textDim, marginBottom: 10, lineHeight: 1.4 }}>
            Record future monthly salary changes. The calendar will calculate paychecks using the new amount on and after the effective date. Baseline salary (used until any scheduled change) is set on the Bills tab.
          </div>
          {showAddSalaryChange && (
            <div style={{ background: T().inputBg, borderRadius: 8, padding: 12, marginBottom: 10, border: "1px solid " + T().inputBd }}>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, color: T().textMuted, marginBottom: 3 }}>Effective Date</div>
                  <input type="date" min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split("T")[0]} value={newSalaryDate} onChange={function (e) { setNewSalaryDate(e.target.value); }} style={{ ...dateInp, fontSize: 12, padding: "6px 8px" }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, color: T().textMuted, marginBottom: 3 }}>New Monthly Salary</div>
                  <input type="number" placeholder="0" value={newSalaryAmount} onChange={function (e) { setNewSalaryAmount(e.target.value); }} style={{ ...addInp, width: "100%", fontSize: 12, padding: "6px 8px", textAlign: "right" }} />
                </div>
              </div>
              <button onClick={function () {
                var amt = parseFloat(newSalaryAmount);
                if (!newSalaryDate || !amt || amt < 0) return;
                var today = new Date(); today.setHours(0, 0, 0, 0);
                var ed = new Date(newSalaryDate + "T12:00:00");
                if (ed <= today) return;
                var entry = { id: Date.now(), effectiveDate: newSalaryDate, newAmount: amt };
                setSalaryChanges(function (prev) { return [...prev, entry]; });
                setNewSalaryDate(""); setNewSalaryAmount(""); setShowAddSalaryChange(false);
              }} style={{ width: "100%", padding: "7px 14px", borderRadius: 6, border: "none", background: T().btnBg, color: T().btnText, cursor: "pointer", fontSize: 12, fontFamily: "system-ui", fontWeight: 600 }}>Save Change</button>
            </div>
          )}
          {salaryChanges.length === 0 ? (
            <div style={{ fontSize: 11, color: T().textDim, fontStyle: "italic", padding: "6px 0" }}>No scheduled changes. Baseline salary of {fm(income.salary.current)}/mo applies indefinitely.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {salaryChanges.slice().sort(function (a, b) { return a.effectiveDate.localeCompare(b.effectiveDate); }).map(function (c, idx) {
                var d = new Date(c.effectiveDate + "T12:00:00");
                var dateStr = (d.getMonth() + 1) + "/" + d.getDate() + "/" + d.getFullYear();
                return (
                  <div key={c.id} style={{ background: idx % 2 === 0 ? rowEven : rowOdd, borderRadius: 6, padding: "8px 10px", display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ flex: 1, fontSize: 12, color: T().textPrimary }}>
                      Starting <span style={{ fontFamily: "monospace", color: T().accent }}>{dateStr}</span>: <span style={{ fontFamily: "monospace", color: T().green, fontWeight: 600 }}>{fm(c.newAmount)}</span>/mo
                    </div>
                    {confirmDelSalaryChange === c.id ? (
                      <div style={{ display: "flex", gap: 4 }}>
                        <button onClick={function () { setConfirmDelSalaryChange(null); }} style={{ background: "transparent", border: "1px solid " + T().inputBd, borderRadius: 4, color: T().textDim, fontSize: 9, padding: "2px 6px", cursor: "pointer", fontFamily: "system-ui" }}>No</button>
                        <button onClick={function () { setSalaryChanges(function (prev) { return prev.filter(function (x) { return x.id !== c.id; }); }); setConfirmDelSalaryChange(null); }} style={{ background: T().red, border: "none", borderRadius: 4, color: "#fff", fontSize: 9, padding: "2px 6px", cursor: "pointer", fontFamily: "system-ui" }}>Yes</button>
                      </div>
                    ) : (
                      <button onClick={function () { setConfirmDelSalaryChange(c.id); }} style={{ background: "none", border: "none", color: T().red, cursor: "pointer", fontSize: 14, padding: "0 4px" }}>×</button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Appearance */}
      <div style={{ background: cardBg, borderRadius: 12, border: cardBd, overflow: "hidden", marginBottom: 12 }}>
        <div style={{ padding: "12px 14px 8px" }}>
          <span style={{ fontSize: 11, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>Appearance</span>
        </div>
        <div style={{ padding: "4px 14px 14px" }}>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={function () { setThemeMode("dark"); }} style={{ flex: 1, padding: "10px", borderRadius: 8, border: "1px solid " + (themeMode === "dark" ? "rgba(74,143,231,0.35)" : "rgba(100,160,255,0.08)"), background: themeMode === "dark" ? "rgba(74,143,231,0.12)" : "transparent", color: themeMode === "dark" ? T().accent : T().textDim, cursor: "pointer", fontSize: 13, fontFamily: "system-ui", fontWeight: themeMode === "dark" ? 600 : 400 }}>Dark Mode</button>
            <button onClick={function () { setThemeMode("light"); }} style={{ flex: 1, padding: "10px", borderRadius: 8, border: "1px solid " + (themeMode === "light" ? "rgba(37,99,235,0.35)" : "rgba(100,160,255,0.08)"), background: themeMode === "light" ? "rgba(37,99,235,0.12)" : "transparent", color: themeMode === "light" ? "#2563eb" : T().textDim, cursor: "pointer", fontSize: 13, fontFamily: "system-ui", fontWeight: themeMode === "light" ? 600 : 400 }}>Light Mode</button>
          </div>
        </div>
      </div>

      {/* Payroll Config */}
      <div style={{ background: cardBg, borderRadius: 12, border: cardBd, overflow: "hidden", marginBottom: 12 }}>
        <div style={{ padding: "12px 14px 8px" }}>
          <span style={{ fontSize: 11, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>Salary Payments</span>
        </div>
        <div style={{ padding: "4px 14px 14px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <div style={labelStyle}>Pay Frequency</div>
              <select value={payroll.frequency} onChange={function (e) { setPayroll(function (prev) { return { ...prev, frequency: e.target.value }; }); }} style={selStyle}>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Bi-weekly (every 2 weeks)</option>
                <option value="bimonthly">Bi-monthly (twice a month)</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div>
              <div style={labelStyle}>First Pay Date</div>
              <input type="date" value={payroll.firstPayDate} onChange={function (e) { setPayroll(function (prev) { return { ...prev, firstPayDate: e.target.value }; }); }} style={dateInp} />
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={labelStyle}>VA Monthly Amount (paid 1st of each month)</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              <div>
                <div style={{ fontSize: 9, color: T().textMuted, marginBottom: 3 }}>Current</div>
                <NumInput value={income.va.current} onChange={function (v) { setIncome(function (prev) { return { ...prev, va: { ...prev.va, current: v } }; }); }} style={{ ...inp, width: "100%" }} />
              </div>
              <div>
                <div style={{ fontSize: 9, color: T().textMuted, marginBottom: 3 }}>Future 1</div>
                <NumInput value={income.va.future1} onChange={function (v) { setIncome(function (prev) { return { ...prev, va: { ...prev.va, future1: v } }; }); }} style={{ ...inp, width: "100%" }} />
              </div>
              <div>
                <div style={{ fontSize: 9, color: T().textMuted, marginBottom: 3 }}>Future 2</div>
                <NumInput value={income.va.future2} onChange={function (v) { setIncome(function (prev) { return { ...prev, va: { ...prev.va, future2: v } }; }); }} style={{ ...inp, width: "100%" }} />
              </div>
            </div>
          </div>

          <div style={{ background: "rgba(100,160,255,0.04)", borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ fontSize: 10, color: T().textMuted, textTransform: "uppercase", fontWeight: 600, marginBottom: 6 }}>How it works</div>
            <div style={{ fontSize: 11, color: T().textMuted, lineHeight: 1.5, marginBottom: 6 }}>{freqLabel}</div>
            <div style={{ fontSize: 11, color: T().textMuted, lineHeight: 1.5, marginBottom: 4 }}>Each paycheck = {payPerCheck}</div>
            <div style={{ fontSize: 11, color: payroll.frequency === "biweekly" ? T().green : T().orange, lineHeight: 1.5, marginBottom: 8 }}>
              {payroll.frequency === "weekly" ? "Annual projection includes extra paychecks (52 weeks = 4 extra weekly checks beyond 48)" : payroll.frequency === "biweekly" ? "Annual projection includes 1 extra paycheck (26 pay periods = 2 extra checks, 1 already in monthly calc)" : "Annual projection: no extra paycheck added (consistent pay periods each month)"}
            </div>
            <div style={{ fontSize: 10, color: T().textMuted, textTransform: "uppercase", fontWeight: 600, marginBottom: 4 }}>Upcoming Pay Dates</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {previewDates.slice(0, 8).map(function (d, i) {
                return <span key={i} style={{ fontSize: 11, color: T().green, fontFamily: "monospace", background: "rgba(92,184,138,0.08)", padding: "3px 8px", borderRadius: 4 }}>{fd(d)}</span>;
              })}
            </div>

            {(payroll.frequency === "biweekly" || payroll.frequency === "weekly") && (
              <div style={{ marginTop: 12, borderTop: "1px solid rgba(100,160,255,0.08)", paddingTop: 10 }}>
                <div style={{ fontSize: 10, color: T().textMuted, textTransform: "uppercase", fontWeight: 600, marginBottom: 6 }}>{payroll.frequency === "weekly" ? "5-Paycheck" : "3-Paycheck"} Months in {currentYear}</div>
                {tripleMonths.length === 0 ? (
                  <div style={{ fontSize: 11, color: T().textDim }}>No 3-paycheck months found for current settings</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {tripleMonths.map(function (tm, i) {
                      return (
                        <div key={i} style={{ background: "rgba(92,184,138,0.06)", borderRadius: 6, padding: "8px 10px", border: "1px solid rgba(92,184,138,0.12)" }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: T().green, marginBottom: 4 }}>{monthNames[tm.month]}</div>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {tm.dates.map(function (d, j) {
                              return <span key={j} style={{ fontSize: 11, color: T().textSecondary, fontFamily: "monospace", background: "rgba(100,160,255,0.06)", padding: "2px 6px", borderRadius: 3 }}>{fd(d)}</span>;
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div style={{ fontSize: 10, color: T().textDim, marginTop: 6 }}>These months give you an extra paycheck to put toward savings, debt, or big expenses.</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bill Settings */}
      <div style={{ background: cardBg, borderRadius: 12, border: cardBd, overflow: "hidden", marginBottom: 12 }}>
        <div style={{ padding: "12px 14px 8px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <span style={{ fontSize: 11, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>Bill Settings</span>
            <div style={{ fontSize: 10, color: T().textDim, marginTop: 4 }}>Edit names, amounts, due dates. Hide removes a bill from the Bills tab (Calendar unaffected). Start/end dates limit when it appears on the Calendar. Same-day bills auto-separate.</div>
          </div>
          <button onClick={function () { setShowAddBill(!showAddBill); }} style={{ background: "rgba(74,143,231,0.1)", border: "1px solid rgba(74,143,231,0.25)", borderRadius: 6, padding: "5px 12px", color: T().accent, cursor: "pointer", fontSize: 11, fontFamily: "system-ui", fontWeight: 600, whiteSpace: "nowrap" }}>+ Add Bill</button>
        </div>
        {showAddBill && (
          <div style={{ padding: "0 14px 10px", display: "flex", gap: 6, flexWrap: "wrap" }}>
            <input placeholder="Bill name" value={nbName} onChange={function (e) { setNbName(e.target.value); }} style={{ ...addInp, flex: 2, textAlign: "left" }} />
            <input placeholder="Amount" value={nbAmt} onChange={function (e) { setNbAmt(e.target.value); }} style={{ ...addInp, flex: 1, textAlign: "right" }} />
            <input placeholder="Due day" value={nbDay} onChange={function (e) { setNbDay(e.target.value); }} style={{ ...addInp, flex: 1, textAlign: "right", maxWidth: 70 }} />
            <button onClick={function () { if (!nbName) return; var newBill = { id: Date.now(), name: nbName, current: parseFloat(nbAmt) || 0, future1: parseFloat(nbAmt) || 0, future2: parseFloat(nbAmt) || 0, dueDay: parseInt(nbDay) || 1, startDate: null, endDate: null, hidden: false }; setBills(function (prev) { return [...prev, newBill]; }); setNbName(""); setNbAmt(""); setNbDay(""); setShowAddBill(false); }} style={{ padding: "7px 14px", borderRadius: 6, border: "none", background: T().btnBg, color: T().btnText, cursor: "pointer", fontSize: 12, fontFamily: "system-ui", fontWeight: 600, width: "100%" }}>Add</button>
          </div>
        )}
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(100,160,255,0.08)" }}>
                <th style={{ ...hs, textAlign: "left", paddingLeft: 10, width: 24 }}></th>
                <th style={{ ...hs, textAlign: "left" }}>Name</th>
                <th style={hs}>Hide</th>
                <th style={hs}>Current</th>
                <th style={hs}>Future 1</th>
                <th style={hs}>Future 2</th>
                <th style={hs}>Due Date</th>
                <th style={hs}>Start Date</th>
                <th style={{ ...hs, paddingRight: 14 }}>End Date</th>
              </tr>
            </thead>
            <tbody>
              {bills.slice().sort(function (a, b) { return a.dueDay - b.dueDay; }).map(function (b, idx) {
                return (
                  <tr key={b.id} style={{ borderBottom: rowBd, background: idx % 2 === 0 ? rowEven : rowOdd }}>
                    <td style={{ padding: "6px 2px 6px 10px", textAlign: "center" }}>
                      {confirmDelBill === b.id ? (
                        <button onClick={function () { setBills(function (prev) { return prev.filter(function (x) { return x.id !== b.id; }); }); setConfirmDelBill(null); }} style={{ background: T().red, border: "none", borderRadius: 4, color: "#fff", fontSize: 9, padding: "2px 6px", cursor: "pointer", fontFamily: "system-ui" }}>Yes</button>
                      ) : (
                        <button onClick={function () { setConfirmDelBill(b.id); }} style={{ background: "none", border: "none", color: T().textDim, cursor: "pointer", fontSize: 13, padding: 0, lineHeight: 1 }}>x</button>
                      )}
                    </td>
                    <td style={{ padding: "6px 4px" }}>
                      <TextInput value={b.name} onChange={function (v) { setBills(function (prev) { return prev.map(function (x) { return x.id === b.id ? { ...x, name: v } : x; }); }); }} style={{ ...inp, width: 110, textAlign: "left" }} />
                    </td>
                    <td style={{ ...cs, fontSize: 12 }}>
                      {/* Hide-from-Bills-tab toggle. Same visual pattern as
                          the card 0% toggle. Hidden bills drop out of the
                          Bills tab list/totals/pie; the Calendar still shows
                          them per their start/end dates. */}
                      <button onClick={function () { setBills(function (prev) { return prev.map(function (x) { return x.id === b.id ? { ...x, hidden: !b.hidden } : x; }); }); }} style={{ background: b.hidden ? "rgba(92,184,138,0.15)" : "rgba(100,160,255,0.05)", border: "1px solid " + (b.hidden ? "rgba(92,184,138,0.3)" : "rgba(100,160,255,0.1)"), borderRadius: 4, color: b.hidden ? T().green : T().textDim, padding: "3px 6px", cursor: "pointer", fontSize: 10, fontFamily: "system-ui" }}>{b.hidden ? "Yes" : "No"}</button>
                    </td>
                    <td style={{ ...cs, fontSize: 12 }}>
                      <NumInput value={b.current} onChange={function (v) { setBills(function (prev) { return prev.map(function (x) { return x.id === b.id ? { ...x, current: v } : x; }); }); }} style={{ ...inp, width: 70 }} />
                    </td>
                    <td style={{ ...cs, fontSize: 12 }}>
                      <NumInput value={b.future1} onChange={function (v) { setBills(function (prev) { return prev.map(function (x) { return x.id === b.id ? { ...x, future1: v } : x; }); }); }} style={{ ...inp, width: 70 }} />
                    </td>
                    <td style={{ ...cs, fontSize: 12 }}>
                      <NumInput value={b.future2} onChange={function (v) { setBills(function (prev) { return prev.map(function (x) { return x.id === b.id ? { ...x, future2: v } : x; }); }); }} style={{ ...inp, width: 70 }} />
                    </td>
                    <td style={{ ...cs, fontSize: 12 }}>
                      <NumInput value={b.dueDay} onChange={function (v) { setBills(function (prev) { return prev.map(function (x) { return x.id === b.id ? { ...x, dueDay: Math.max(1, Math.min(28, Math.round(v))) } : x; }); }); }} style={{ ...inp, width: 45 }} />
                    </td>
                    <td style={{ ...cs, fontSize: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}>
                        <input
                          type="date"
                          value={b.startDate || ""}
                          onChange={function (e) {
                            var v = e.target.value || null;
                            setBills(function (prev) { return prev.map(function (x) { return x.id === b.id ? { ...x, startDate: v } : x; }); });
                          }}
                          style={{ ...inp, width: 118, textAlign: "left", fontSize: 11, color: b.startDate ? T().textPrimary : T().textDim }}
                        />
                        {b.startDate && (
                          <button
                            title="Clear start date"
                            onClick={function () { setBills(function (prev) { return prev.map(function (x) { return x.id === b.id ? { ...x, startDate: null } : x; }); }); }}
                            style={{ background: "none", border: "none", color: T().textDim, cursor: "pointer", fontSize: 12, padding: "0 2px", lineHeight: 1 }}
                          >×</button>
                        )}
                      </div>
                      {b.startDate && b.startDate > localISO(new Date()) && (
                        <div style={{ fontSize: 8, color: T().accent, marginTop: 2, textAlign: "right" }}>starts {new Date(b.startDate + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
                      )}
                      {b.startDate && b.endDate && b.startDate > b.endDate && (
                        <div style={{ fontSize: 8, color: T().red, marginTop: 2, textAlign: "right" }}>start after end — never active</div>
                      )}
                    </td>
                    <td style={{ ...cs, fontSize: 12, paddingRight: 14 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}>
                        <input
                          type="date"
                          value={b.endDate || ""}
                          onChange={function (e) {
                            var v = e.target.value || null;
                            setBills(function (prev) { return prev.map(function (x) { return x.id === b.id ? { ...x, endDate: v } : x; }); });
                          }}
                          style={{ ...inp, width: 118, textAlign: "left", fontSize: 11, color: b.endDate ? T().textPrimary : T().textDim }}
                        />
                        {b.endDate && (
                          <button
                            title="Clear end date"
                            onClick={function () { setBills(function (prev) { return prev.map(function (x) { return x.id === b.id ? { ...x, endDate: null } : x; }); }); }}
                            style={{ background: "none", border: "none", color: T().textDim, cursor: "pointer", fontSize: 12, padding: "0 2px", lineHeight: 1 }}
                          >×</button>
                        )}
                      </div>
                      {b.endDate && b.endDate < localISO(new Date()) && (
                        <div style={{ fontSize: 8, color: T().orange, marginTop: 2, textAlign: "right" }}>ended — no longer on calendar</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Card Settings */}
      <div style={{ background: cardBg, borderRadius: 12, border: cardBd, overflow: "hidden" }}>
        <div style={{ padding: "12px 14px 8px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <span style={{ fontSize: 11, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>Card Settings</span>
            <div style={{ fontSize: 10, color: T().textDim, marginTop: 4 }}>Edit names, balances, APR, due dates, min %. Pay Down shows the payment needed to reach your target max balance. Changes reflect everywhere.</div>
          </div>
          <button onClick={function () { setShowAddCard(!showAddCard); }} style={{ background: "rgba(74,143,231,0.1)", border: "1px solid rgba(74,143,231,0.25)", borderRadius: 6, padding: "5px 12px", color: T().accent, cursor: "pointer", fontSize: 11, fontFamily: "system-ui", fontWeight: 600, whiteSpace: "nowrap" }}>+ Add Card</button>
        </div>
        {/* Target utilization selector — drives the "Pay Down" column below.
            Persisted (cardTargetPct) so the chosen target survives reloads. */}
        <div style={{ padding: "0 14px 10px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 10, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>Target Max Balance</span>
          <div style={{ display: "flex", gap: 4 }}>
            {[8, 25, 28, 50].map(function (pct) {
              var isSel = cardTargetPct === pct;
              return (
                <button
                  key={pct}
                  onClick={function () { setCardTargetPct(pct); }}
                  style={{
                    padding: "4px 10px", borderRadius: 6,
                    border: "1px solid " + (isSel ? "rgba(74,143,231,0.4)" : "rgba(100,160,255,0.1)"),
                    background: isSel ? "rgba(74,143,231,0.15)" : "transparent",
                    color: isSel ? T().accent : T().textDim,
                    cursor: "pointer", fontSize: 11, fontFamily: "monospace",
                    fontWeight: isSel ? 700 : 400,
                  }}
                >{pct}%</button>
              );
            })}
          </div>
          <span style={{ fontSize: 10, color: T().textDim }}>Pay Down shows what each card needs to reach {cardTargetPct}% of its limit.</span>
        </div>
        {showAddCard && (
          <div style={{ padding: "0 14px 10px", display: "flex", gap: 6, flexWrap: "wrap" }}>
            <input placeholder="Card name" value={ncName} onChange={function (e) { setNcName(e.target.value); }} style={{ ...addInp, flex: 2, textAlign: "left" }} />
            <input placeholder="Balance" value={ncBal} onChange={function (e) { setNcBal(e.target.value); }} style={{ ...addInp, flex: 1, textAlign: "right" }} />
            <input placeholder="Limit" value={ncLim} onChange={function (e) { setNcLim(e.target.value); }} style={{ ...addInp, flex: 1, textAlign: "right" }} />
            <input placeholder="APR %" value={ncApr} onChange={function (e) { setNcApr(e.target.value); }} style={{ ...addInp, flex: 1, textAlign: "right" }} />
            <input placeholder="Due day" value={ncDay} onChange={function (e) { setNcDay(e.target.value); }} style={{ ...addInp, flex: 1, textAlign: "right", maxWidth: 70 }} />
            <button onClick={function () { if (!ncName) return; var newCard = { id: Date.now(), name: ncName, payment: 0, balance: parseFloat(ncBal) || 0, limit: parseFloat(ncLim) || 0, dueDay: parseInt(ncDay) || 1, apr: parseFloat(ncApr) || 0, zero: false, minPct: 2, mode: "used" }; setCards(function (prev) { return [...prev, newCard]; }); setNcName(""); setNcBal(""); setNcLim(""); setNcApr(""); setNcDay(""); setShowAddCard(false); }} style={{ padding: "7px 14px", borderRadius: 6, border: "none", background: T().btnBg, color: T().btnText, cursor: "pointer", fontSize: 12, fontFamily: "system-ui", fontWeight: 600, width: "100%" }}>Add</button>
          </div>
        )}
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 660 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(100,160,255,0.08)" }}>
                <th style={{ ...hs, textAlign: "left", paddingLeft: 10, width: 24 }}></th>
                <th style={{ ...hs, textAlign: "left" }}>Name</th>
                <th style={hs}>Balance</th>
                <th style={hs}>Limit</th>
                <th style={hs}>Pay Down</th>
                <th style={hs}>APR %</th>
                <th style={hs}>Min %</th>
                <th style={hs}>Due</th>
                <th style={hs}>0%</th>
                <th style={hs}>Min Pmt</th>
                <th style={{ ...hs, paddingRight: 14 }}>Int/Mo</th>
              </tr>
            </thead>
            <tbody>
              {cards.slice().sort(function (a, b) { return a.dueDay - b.dueDay; }).map(function (c, idx) {
                var monthInt = c.apr > 0 && !c.zero ? c.balance * (c.apr / 100 / 12) : 0;
                var u = c.limit > 0 ? c.balance / c.limit : 0;
                // Amount to pay to bring this card down to the target
                // utilization. null = not computable (no limit set).
                var payDown = c.limit > 0 ? Math.max(0, c.balance - c.limit * (cardTargetPct / 100)) : null;
                return (
                  <tr key={c.id} style={{ borderBottom: rowBd, background: utilRowBg(u) }}>
                    <td style={{ padding: "6px 2px 6px 10px", textAlign: "center" }}>
                      {confirmDelCard === c.id ? (
                        <button onClick={function () { setCards(function (prev) { return prev.filter(function (x) { return x.id !== c.id; }); }); setConfirmDelCard(null); }} style={{ background: T().red, border: "none", borderRadius: 4, color: "#fff", fontSize: 9, padding: "2px 6px", cursor: "pointer", fontFamily: "system-ui" }}>Yes</button>
                      ) : (
                        <button onClick={function () { setConfirmDelCard(c.id); }} style={{ background: "none", border: "none", color: T().textDim, cursor: "pointer", fontSize: 13, padding: 0, lineHeight: 1 }}>x</button>
                      )}
                    </td>
                    <td style={{ padding: "6px 4px" }}>
                      <TextInput value={c.name} onChange={function (v) { setCards(function (prev) { return prev.map(function (x) { return x.id === c.id ? { ...x, name: v } : x; }); }); }} style={{ ...inp, width: 100, textAlign: "left" }} />
                    </td>
                    <td style={{ ...cs, fontSize: 12 }}>
                      <NumInput value={c.balance} onChange={function (v) { setCards(function (prev) { return prev.map(function (x) { return x.id === c.id ? { ...x, balance: v } : x; }); }); }} style={{ ...inp, width: 70 }} />
                    </td>
                    <td style={{ ...cs, fontSize: 12 }}>
                      <NumInput value={c.limit} onChange={function (v) { setCards(function (prev) { return prev.map(function (x) { return x.id === c.id ? { ...x, limit: v } : x; }); }); }} style={{ ...inp, width: 65 }} />
                    </td>
                    <td style={{ ...cs, fontSize: 12, fontFamily: "monospace", color: payDown === null ? T().textDim : payDown > 0 ? T().orange : T().green, fontWeight: payDown !== null && payDown > 0 ? 700 : 400 }}>
                      {payDown === null ? "—" : payDown > 0 ? fm(Math.ceil(payDown)) : "✓"}
                    </td>
                    <td style={{ ...cs, fontSize: 12 }}>
                      <NumInput value={c.apr} onChange={function (v) { setCards(function (prev) { return prev.map(function (x) { return x.id === c.id ? { ...x, apr: v } : x; }); }); }} style={{ ...inp, width: 55 }} />
                    </td>
                    <td style={{ ...cs, fontSize: 12 }}>
                      <NumInput value={c.minPct} onChange={function (v) { setCards(function (prev) { return prev.map(function (x) { return x.id === c.id ? { ...x, minPct: v } : x; }); }); }} style={{ ...inp, width: 45 }} />
                    </td>
                    <td style={{ ...cs, fontSize: 12 }}>
                      <NumInput value={c.dueDay} onChange={function (v) { setCards(function (prev) { return prev.map(function (x) { return x.id === c.id ? { ...x, dueDay: Math.round(v) || 1 } : x; }); }); }} style={{ ...inp, width: 40 }} />
                    </td>
                    <td style={{ ...cs, fontSize: 12, textAlign: "center" }}>
                      <button onClick={function () { setCards(function (prev) { return prev.map(function (x) { return x.id === c.id ? { ...x, zero: !c.zero } : x; }); }); }} style={{ background: c.zero ? "rgba(92,184,138,0.15)" : "rgba(100,160,255,0.05)", border: "1px solid " + (c.zero ? "rgba(92,184,138,0.3)" : "rgba(100,160,255,0.1)"), borderRadius: 4, color: c.zero ? T().green : T().textDim, padding: "3px 6px", cursor: "pointer", fontSize: 10, fontFamily: "system-ui" }}>{c.zero ? "Yes" : "No"}</button>
                    </td>
                    <td style={{ ...cs, fontSize: 12, color: estPayment(c.balance, c.apr, c.minPct, c.zero) > 0 ? T().textPrimary : T().textDim }}>{estPayment(c.balance, c.apr, c.minPct, c.zero) > 0 ? fm(estPayment(c.balance, c.apr, c.minPct, c.zero)) : "-"}</td>
                    <td style={{ ...cs, fontSize: 12, paddingRight: 14, color: monthInt > 0 ? T().red : T().textDim }}>{monthInt > 0 ? fm(monthInt) : "-"}</td>
                  </tr>
                );
              })}
              <tr style={{ borderTop: secBd, background: "rgba(100,160,255,0.04)" }}>
                <td style={{ padding: "7px 2px 7px 10px" }}></td>
                <td style={{ padding: "7px 4px", fontSize: 12, fontWeight: 700, color: T().textSecondary, textAlign: "left" }}>TOTAL</td>
                <td style={{ ...cs, fontSize: 12, fontWeight: 700, color: T().red }}>{fm(cards.reduce(function (s, c) { return s + c.balance; }, 0))}</td>
                <td style={{ ...cs, fontSize: 12, color: T().textMuted }}>{fm(cards.reduce(function (s, c) { return s + c.limit; }, 0))}</td>
                <td style={{ ...cs, fontSize: 12, fontWeight: 700, color: T().orange }}>{fm(Math.ceil(cards.reduce(function (s, c) { return s + (c.limit > 0 ? Math.max(0, c.balance - c.limit * (cardTargetPct / 100)) : 0); }, 0)))}</td>
                <td style={cs}></td>
                <td style={cs}></td>
                <td style={cs}></td>
                <td style={cs}></td>
                <td style={{ ...cs, fontSize: 12, fontWeight: 700 }}>{fm(cards.reduce(function (s, c) { return s + estPayment(c.balance, c.apr, c.minPct, c.zero); }, 0))}</td>
                <td style={{ ...cs, fontSize: 12, paddingRight: 14, fontWeight: 700, color: T().red }}>{fm(cards.reduce(function (s, c) { return s + (c.apr > 0 && !c.zero ? c.balance * (c.apr / 100 / 12) : 0); }, 0))}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ background: cardBg, borderRadius: 12, border: cardBd, padding: 14, marginTop: 12 }}>
        <div style={{ fontSize: 11, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700, marginBottom: 8 }}>How Estimated Payments Work</div>
        <div style={{ fontSize: 12, color: T().textMuted, lineHeight: 1.6 }}>
          Min Pmt = the greater of (Balance x Min %) or $25. Adjust the Min % to match your statement. This amount is used for calendar projections, leftover calculations, and debt payoff estimates.
        </div>
      </div>

      {/* Security & User Management */}
      <div style={{ background: cardBg, borderRadius: 12, border: cardBd, overflow: "hidden", marginTop: 12 }}>
        <div style={{ padding: "12px 14px 8px" }}>
          <span style={{ fontSize: 11, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>Security</span>
          <div style={{ fontSize: 10, color: T().textDim, marginTop: 4 }}>Logged in as: <span style={{ color: T().textSecondary, fontFamily: "monospace" }}>{currentUser ? currentUser.username : ""}</span>{currentUser && currentUser.is_admin ? " (admin)" : ""}</div>
        </div>
        <div style={{ padding: "4px 14px 14px" }}>
          {/* Change own PIN */}
          <button onClick={function () { setShowPinChange(!showPinChange); setPinMsg(""); setNewPin(""); setConfirmPin(""); }} style={{ background: "rgba(74,143,231,0.1)", border: "1px solid rgba(74,143,231,0.25)", borderRadius: 6, padding: "7px 14px", color: T().accent, cursor: "pointer", fontSize: 12, fontFamily: "system-ui", fontWeight: 600, width: "100%", marginBottom: 8 }}>Change My PIN</button>
          {showPinChange && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 12, padding: "10px", background: "rgba(100,160,255,0.04)", borderRadius: 8 }}>
              <div>
                <div style={{ fontSize: 11, color: T().textMuted, marginBottom: 3 }}>New PIN (4-6 digits)</div>
                <input type="password" value={newPin} onChange={function (e) { setNewPin(e.target.value.replace(/[^0-9]/g, "").slice(0, 6)); }} placeholder="New PIN" style={{ width: "100%", padding: "8px", background: T().inputBg, border: "1px solid " + T().inputBd, borderRadius: 6, color: T().textPrimary, fontSize: 14, fontFamily: "monospace", outline: "none", boxSizing: "border-box", textAlign: "center", letterSpacing: 6 }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: T().textMuted, marginBottom: 3 }}>Confirm New PIN</div>
                <input type="password" value={confirmPin} onChange={function (e) { setConfirmPin(e.target.value.replace(/[^0-9]/g, "").slice(0, 6)); }} placeholder="Confirm" onKeyDown={function (e) { if (e.key === "Enter") {
                  if (newPin.length < 4) { setPinMsg("PIN must be at least 4 digits"); return; }
                  if (newPin !== confirmPin) { setPinMsg("PINs don't match"); return; }
                  updateUserPin(currentUser.id, newPin).then(function (ok) {
                    setPinMsg(ok ? "PIN updated!" : "Failed");
                    if (ok) { setNewPin(""); setConfirmPin(""); setTimeout(function () { setShowPinChange(false); setPinMsg(""); }, 1500); }
                  });
                }}} style={{ width: "100%", padding: "8px", background: T().inputBg, border: "1px solid " + T().inputBd, borderRadius: 6, color: T().textPrimary, fontSize: 14, fontFamily: "monospace", outline: "none", boxSizing: "border-box", textAlign: "center", letterSpacing: 6 }} />
              </div>
              {pinMsg && <div style={{ fontSize: 11, color: pinMsg.includes("updated") ? T().green : T().red, textAlign: "center" }}>{pinMsg}</div>}
              <button onClick={function () {
                if (newPin.length < 4) { setPinMsg("PIN must be at least 4 digits"); return; }
                if (newPin !== confirmPin) { setPinMsg("PINs don't match"); return; }
                updateUserPin(currentUser.id, newPin).then(function (ok) {
                  setPinMsg(ok ? "PIN updated!" : "Failed");
                  if (ok) { setNewPin(""); setConfirmPin(""); setTimeout(function () { setShowPinChange(false); setPinMsg(""); }, 1500); }
                });
              }} style={{ padding: "8px", borderRadius: 6, border: "none", background: T().btnBg, color: T().btnText, cursor: "pointer", fontSize: 13, fontFamily: "system-ui", fontWeight: 600 }}>Update PIN</button>
            </div>
          )}
        </div>
      </div>

      {/* Admin: User Management */}
      {currentUser && currentUser.is_admin && (
        <div style={{ background: cardBg, borderRadius: 12, border: cardBd, overflow: "hidden", marginTop: 12 }}>
          <div style={{ padding: "12px 14px 8px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <span style={{ fontSize: 11, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>User Management</span>
              <div style={{ fontSize: 10, color: T().textDim, marginTop: 4 }}>Admin only. Create accounts for others with blank data.</div>
            </div>
            <button onClick={function () { setShowAddUser(!showAddUser); setUserMsg(""); }} style={{ background: "rgba(92,184,138,0.1)", border: "1px solid rgba(92,184,138,0.25)", borderRadius: 6, padding: "5px 12px", color: T().green, cursor: "pointer", fontSize: 11, fontFamily: "system-ui", fontWeight: 600, whiteSpace: "nowrap" }}>+ Add User</button>
          </div>

          {showAddUser && (
            <div style={{ padding: "0 14px 12px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "10px", background: "rgba(92,184,138,0.04)", borderRadius: 8 }}>
                <input placeholder="Username" value={newUsername} onChange={function (e) { setNewUsername(e.target.value); }} style={{ width: "100%", padding: "8px", background: T().inputBg, border: "1px solid " + T().inputBd, borderRadius: 6, color: T().textPrimary, fontSize: 13, fontFamily: "monospace", outline: "none", boxSizing: "border-box" }} />
                <input type="password" placeholder="PIN (4-6 digits)" value={newUserPin} onChange={function (e) { setNewUserPin(e.target.value.replace(/[^0-9]/g, "").slice(0, 6)); }} style={{ width: "100%", padding: "8px", background: T().inputBg, border: "1px solid " + T().inputBd, borderRadius: 6, color: T().textPrimary, fontSize: 13, fontFamily: "monospace", outline: "none", boxSizing: "border-box", textAlign: "center", letterSpacing: 6 }} />
                <input type="password" placeholder="Confirm PIN" value={newUserConfirm} onChange={function (e) { setNewUserConfirm(e.target.value.replace(/[^0-9]/g, "").slice(0, 6)); }} style={{ width: "100%", padding: "8px", background: T().inputBg, border: "1px solid " + T().inputBd, borderRadius: 6, color: T().textPrimary, fontSize: 13, fontFamily: "monospace", outline: "none", boxSizing: "border-box", textAlign: "center", letterSpacing: 6 }} />
                {userMsg && <div style={{ fontSize: 11, color: userMsg.includes("Created") ? T().green : T().red, textAlign: "center" }}>{userMsg}</div>}
                <button onClick={function () {
                  if (!newUsername.trim()) { setUserMsg("Enter a username"); return; }
                  if (newUserPin.length < 4) { setUserMsg("PIN must be at least 4 digits"); return; }
                  if (newUserPin !== newUserConfirm) { setUserMsg("PINs don't match"); return; }
                  createUser(newUsername, newUserPin, false).then(function (result) {
                    if (result.success) {
                      setUserMsg("Created " + newUsername + "!");
                      setNewUsername(""); setNewUserPin(""); setNewUserConfirm("");
                      listUsers().then(function (u) { setUsers(u); });
                      setTimeout(function () { setShowAddUser(false); setUserMsg(""); }, 1500);
                    } else {
                      setUserMsg(result.error);
                    }
                  });
                }} style={{ padding: "8px", borderRadius: 6, border: "none", background: T().btnBg, color: T().btnText, cursor: "pointer", fontSize: 13, fontFamily: "system-ui", fontWeight: 600 }}>Create User</button>
              </div>
            </div>
          )}

          {/* Users list */}
          <div style={{ padding: "0 14px 14px" }}>
            {users.map(function (u, idx) {
              var isMe = currentUser && u.id === currentUser.id;
              return (
                <div key={u.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: idx % 2 === 0 ? rowEven : rowOdd, borderRadius: 8, marginBottom: 4 }}>
                  <div>
                    <div style={{ fontSize: 13, color: isMe ? T().accent : T().textSecondary, fontWeight: 500, fontFamily: "monospace" }}>{u.username}{isMe ? " (you)" : ""}</div>
                    <div style={{ fontSize: 10, color: T().textDim, marginTop: 1 }}>{u.is_admin ? "Admin" : "User"} | Created: {new Date(u.created_at).toLocaleDateString()}</div>
                  </div>
                  {!isMe && (
                    <div>
                      {confirmDelUser === u.id ? (
                        <div style={{ display: "flex", gap: 6 }}>
                          <button onClick={function () { deleteUser(u.id).then(function () { listUsers().then(function (ul) { setUsers(ul); }); }); setConfirmDelUser(null); }} style={{ background: T().red, border: "none", borderRadius: 4, color: "#fff", fontSize: 10, padding: "4px 8px", cursor: "pointer", fontFamily: "system-ui" }}>Delete</button>
                          <button onClick={function () { setConfirmDelUser(null); }} style={{ background: "none", border: "1px solid " + T().textDim, borderRadius: 4, color: T().textDim, fontSize: 10, padding: "4px 8px", cursor: "pointer", fontFamily: "system-ui" }}>Cancel</button>
                        </div>
                      ) : (
                        <button onClick={function () { setConfirmDelUser(u.id); }} style={{ background: "none", border: "none", color: T().textDim, cursor: "pointer", fontSize: 13, padding: "2px 6px" }}>x</button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* App Maintenance — clears the service-worker cache and reloads. Useful
          when a deploy hasn't propagated, when stale data appears, or when the
          app is otherwise behaving oddly. The button confirms before running
          since unregistering the SW briefly takes the app offline. */}
      <div style={{ background: cardBg, borderRadius: 12, border: cardBd, overflow: "hidden", marginBottom: 12 }}>
        <div style={{ padding: "12px 14px 8px" }}>
          <span style={{ fontSize: 11, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>App Maintenance</span>
        </div>
        <div style={{ padding: "4px 14px 14px" }}>
          <div style={{ fontSize: 11, color: T().textDim, marginBottom: 10, lineHeight: 1.4 }}>
            Force a fresh download of the latest app version. Use this if the app feels stale after a deploy, if changes don't appear, or if you see old data reappear unexpectedly. Your saved data in the cloud is unaffected.
          </div>
          {!confirmRefresh ? (
            <button onClick={function () { setConfirmRefresh(true); }} disabled={refreshing} style={{ background: "rgba(74,143,231,0.1)", border: "1px solid rgba(74,143,231,0.25)", borderRadius: 6, padding: "7px 14px", color: T().accent, cursor: "pointer", fontSize: 12, fontFamily: "system-ui", fontWeight: 600, width: "100%" }}>
              Force Refresh App
            </button>
          ) : (
            <div>
              <div style={{ fontSize: 11, color: T().textPrimary, marginBottom: 8 }}>
                {refreshing ? "Refreshing — the app will reload in a moment..." : "This will clear cached files and reload. Continue?"}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={function () { setConfirmRefresh(false); }} disabled={refreshing} style={{ flex: 1, background: "transparent", border: "1px solid " + T().inputBd, borderRadius: 6, padding: "7px 14px", color: T().textDim, cursor: refreshing ? "default" : "pointer", fontSize: 12, fontFamily: "system-ui", fontWeight: 500 }}>
                  Cancel
                </button>
                <button onClick={async function () {
                  setRefreshing(true);
                  try {
                    if ("serviceWorker" in navigator) {
                      var regs = await navigator.serviceWorker.getRegistrations();
                      await Promise.all(regs.map(function (r) { return r.unregister(); }));
                    }
                    if ("caches" in window) {
                      var keys = await caches.keys();
                      await Promise.all(keys.map(function (k) { return caches.delete(k); }));
                    }
                  } catch (e) {
                    // Even if cleanup fails, still reload — fresh fetch will likely fix it
                    // eslint-disable-next-line no-console
                    console.error("Force refresh cleanup error:", e);
                  }
                  // Small delay so the user sees the "refreshing" message before the reload
                  setTimeout(function () { window.location.reload(); }, 400);
                }} disabled={refreshing} style={{ flex: 1, background: refreshing ? T().inputBg : T().red, border: "none", borderRadius: 6, padding: "7px 14px", color: refreshing ? T().textDim : "#fff", cursor: refreshing ? "default" : "pointer", fontSize: 12, fontFamily: "system-ui", fontWeight: 600 }}>
                  {refreshing ? "Refreshing..." : "Yes, Refresh"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Backup & Restore — exports all user data as a downloadable JSON file
          and lets the user restore from one. The export is decrypted plaintext
          so it's directly inspectable and recoverable without the app. Restore
          overwrites all current state; it confirms before applying. */}
      <div style={{ background: cardBg, borderRadius: 12, border: cardBd, overflow: "hidden", marginBottom: 12 }}>
        <div style={{ padding: "12px 14px 8px" }}>
          <span style={{ fontSize: 11, color: T().textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>Backup & Restore</span>
        </div>
        <div style={{ padding: "4px 14px 14px" }}>
          <div style={{ fontSize: 11, color: T().textDim, marginBottom: 10, lineHeight: 1.4 }}>
            Download a JSON file containing all your bills, cards, income, scheduled changes, transactions, and balances. The file is plaintext — store it somewhere safe (encrypted drive, password manager file vault). Run before any risky operation (Supabase changes, large data edits, app migration).
          </div>
          <button onClick={function () {
            // Assemble export payload from current in-memory state. Including
            // every persisted key plus a version field and timestamp so a
            // future-incompatible payload can be detected on restore.
            var payload = {
              vault_budget_backup: true,
              version: APP_VERSION,
              exportedAt: new Date().toISOString(),
              username: currentUser && currentUser.username ? currentUser.username : null,
              data: {
                bills: bills,
                cards: cards,
                income: income,
                payroll: payroll,
                oneTimeTxs: oneTimeTxs,
                vaChanges: vaChanges,
                salaryChanges: salaryChanges,
                startingBalance: startingBalance,
                savingsBalance: savingsBalance,
                themeMode: themeMode,
                scenarios: scenarios,
                cardTargetPct: cardTargetPct,
              },
            };
            var jsonStr = JSON.stringify(payload, null, 2);
            var blob = new Blob([jsonStr], { type: "application/json" });
            var url = URL.createObjectURL(blob);
            var a = document.createElement("a");
            a.href = url;
            var d = new Date();
            var ts = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
            var uname = (currentUser && currentUser.username) ? currentUser.username : "user";
            a.download = "vault-budget-backup-" + uname + "-" + ts + ".json";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }} style={{ background: "rgba(74,143,231,0.1)", border: "1px solid rgba(74,143,231,0.25)", borderRadius: 6, padding: "7px 14px", color: T().accent, cursor: "pointer", fontSize: 12, fontFamily: "system-ui", fontWeight: 600, width: "100%", marginBottom: 8 }}>
            Download Backup
          </button>

          {!confirmRestore ? (
            <div>
              <input type="file" accept="application/json,.json" id="vb-restore-file" style={{ display: "none" }} onChange={function (e) {
                var file = e.target.files && e.target.files[0];
                if (!file) return;
                var reader = new FileReader();
                reader.onload = function (ev) {
                  try {
                    var parsed = JSON.parse(ev.target.result);
                    if (!parsed || parsed.vault_budget_backup !== true || !parsed.data) {
                      setRestoreError("File does not look like a Vault Budget backup.");
                      setRestorePayload(null);
                      return;
                    }
                    setRestoreError("");
                    setRestorePayload(parsed);
                    setConfirmRestore(true);
                  } catch (err) {
                    setRestoreError("Could not parse the file as JSON.");
                    setRestorePayload(null);
                  }
                  // Reset the input so re-picking the same file fires onChange again
                  e.target.value = "";
                };
                reader.readAsText(file);
              }} />
              <button onClick={function () {
                setRestoreError("");
                document.getElementById("vb-restore-file").click();
              }} style={{ background: "transparent", border: "1px solid " + T().inputBd, borderRadius: 6, padding: "7px 14px", color: T().textDim, cursor: "pointer", fontSize: 12, fontFamily: "system-ui", fontWeight: 500, width: "100%" }}>
                Restore from Backup...
              </button>
              {restoreError && <div style={{ fontSize: 11, color: T().red, marginTop: 6 }}>{restoreError}</div>}
            </div>
          ) : (
            <div style={{ border: "1px solid " + T().red, borderRadius: 6, padding: 10, background: "rgba(196,92,92,0.08)" }}>
              <div style={{ fontSize: 11, color: T().red, fontWeight: 700, marginBottom: 6 }}>Confirm restore</div>
              <div style={{ fontSize: 11, color: T().textPrimary, marginBottom: 8, lineHeight: 1.4 }}>
                This will overwrite ALL current data with the contents of the backup file.
                {restorePayload && restorePayload.exportedAt ? (
                  <span> Backup is from <span style={{ fontFamily: "monospace" }}>{new Date(restorePayload.exportedAt).toLocaleString()}</span></span>
                ) : null}
                {restorePayload && restorePayload.username ? (
                  <span> for user <span style={{ fontFamily: "monospace" }}>{restorePayload.username}</span></span>
                ) : null}.
                {currentUser && restorePayload && restorePayload.username && restorePayload.username !== currentUser.username ? (
                  <div style={{ marginTop: 6, color: T().red, fontWeight: 600 }}>Warning: backup is from a different user ({restorePayload.username}) than the current login ({currentUser.username}).</div>
                ) : null}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={function () { setConfirmRestore(false); setRestorePayload(null); }} style={{ flex: 1, background: "transparent", border: "1px solid " + T().inputBd, borderRadius: 6, padding: "7px 14px", color: T().textDim, cursor: "pointer", fontSize: 12, fontFamily: "system-ui", fontWeight: 500 }}>
                  Cancel
                </button>
                <button onClick={function () {
                  if (!restorePayload || !restorePayload.data) return;
                  var d = restorePayload.data;
                  // Apply each key only if it's present in the payload. Each
                  // setter routes through makeUpdater which triggers autoSave
                  // for that key, so the restored state persists to Supabase.
                  if (d.bills !== undefined) setBills(d.bills);
                  if (d.cards !== undefined) setCards(d.cards);
                  if (d.income !== undefined) setIncome(d.income);
                  if (d.payroll !== undefined) setPayroll(d.payroll);
                  if (d.oneTimeTxs !== undefined) setOneTimeTxs(d.oneTimeTxs);
                  if (d.vaChanges !== undefined) setVaChanges(d.vaChanges);
                  if (d.salaryChanges !== undefined) setSalaryChanges(d.salaryChanges);
                  if (d.startingBalance !== undefined) setStartingBalance(d.startingBalance);
                  if (d.savingsBalance !== undefined) setSavingsBalance(d.savingsBalance);
                  if (d.themeMode !== undefined) setThemeMode(d.themeMode);
                  // scenarios: apply only if present in payload (older backups
                  // won't have it). Also reset activeScenarioId so the Calendar
                  // lands on Master — the previously-active scenario may no
                  // longer exist in the restored set.
                  if (d.scenarios !== undefined) setScenarios(d.scenarios);
                  if (d.cardTargetPct !== undefined) setCardTargetPct(d.cardTargetPct);
                  if (setActiveScenarioId) setActiveScenarioId(null);
                  setConfirmRestore(false);
                  setRestorePayload(null);
                }} style={{ flex: 1, background: T().red, border: "none", borderRadius: 6, padding: "7px 14px", color: "#fff", cursor: "pointer", fontSize: 12, fontFamily: "system-ui", fontWeight: 600 }}>
                  Yes, Restore
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* MAIN */
export default function VaultBudget() {
  const [currentUser, setCurrentUser] = useState(null);
  const [tab, setTab] = useState("bills");
  const [bills, setBills] = useState(INIT_BILLS);
  const [income, setIncome] = useState(INIT_INCOME);
  const [cards, setCards] = useState(INIT_CARDS);
  const [payroll, setPayroll] = useState(INIT_PAYROLL);
  const [oneTimeTxs, setOneTimeTxs] = useState([]);
  const [vaChanges, setVaChanges] = useState([]);
  const [salaryChanges, setSalaryChanges] = useState([]);
  const [startingBalance, setStartingBalance] = useState(0);
  const [savingsBalance, setSavingsBalance] = useState(0);
  const [themeMode, setThemeMode] = useState("dark");
  // Calendar scenarios: each scenario is an income overlay (salary, VA, and
  // their scheduled changes) that the Calendar tab can swap in instead of
  // Master's income. Bills/Cards/Payroll/oneTimeTxs/balances always come from
  // Master regardless of which scenario is active. Persisted to Supabase.
  const [scenarios, setScenarios] = useState([]);
  // Target utilization % for the Card Settings "pay down to reach" column.
  // Persisted like other settings. Default 28 matches the preset options.
  const [cardTargetPct, setCardTargetPct] = useState(28);
  // Which scenario the Calendar is currently displaying. null = Master view.
  // Transient (not persisted) — viewing context shouldn't survive a reload.
  const [activeScenarioId, setActiveScenarioId] = useState(null);
  // Projection date is shared across Bills, Debt, and Calendar tabs so the
  // user only picks it once. Transient UI state — intentionally not saved to
  // Supabase, since "what am I looking at right now" shouldn't survive logout.
  const [projDate, setProjDate] = useState("");
  var theme = THEMES[themeMode];
  setActiveTheme(theme);
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  // Tracks keys that are safe to save: either loaded successfully or
  // confirmed-missing (new user, first write). Keys that errored on load
  // are NOT in this set — saving those would risk clobbering good DB data
  // with our empty in-memory state. The user can use Force Refresh to retry.
  var saveableKeys = useRef(new Set());
  // Tracks keys that errored on load (for UI display).
  const [erroredKeys, setErroredKeys] = useState([]);

  // Load data after login
  function handleLogin(user) {
    setCurrentUserId(user.id);
    setCurrentUser(user);
    setLoading(true);
    saveableKeys.current = new Set();
    setErroredKeys([]);
    loadAllData().then(function (result) {
      // result = { ok: { key: value }, missing: [keys], errored: [keys] }
      var data = result.ok;
      if (data.bills !== undefined) setBills(data.bills);
      if (data.cards !== undefined) setCards(data.cards);
      if (data.income !== undefined) setIncome(data.income);
      if (data.payroll !== undefined) setPayroll(data.payroll);
      if (data.oneTimeTxs !== undefined) setOneTimeTxs(data.oneTimeTxs);
      if (data.vaChanges !== undefined) setVaChanges(data.vaChanges);
      if (data.salaryChanges !== undefined) setSalaryChanges(data.salaryChanges);
      if (data.startingBalance !== undefined && data.startingBalance !== null) setStartingBalance(data.startingBalance);
      if (data.savingsBalance !== undefined && data.savingsBalance !== null) setSavingsBalance(data.savingsBalance);
      if (data.themeMode !== undefined) setThemeMode(data.themeMode);
      if (data.scenarios !== undefined) setScenarios(data.scenarios);
      if (data.cardTargetPct !== undefined) setCardTargetPct(data.cardTargetPct);

      // Successfully-loaded keys are saveable
      Object.keys(data).forEach(function (k) { saveableKeys.current.add(k); });
      // Missing keys are saveable too (new-user / first-write case)
      result.missing.forEach(function (k) { saveableKeys.current.add(k); });
      // Errored keys are NOT saveable until resolved
      setErroredKeys(result.errored);
      if (result.errored.length > 0) {
        // eslint-disable-next-line no-console
        console.warn("Some keys failed to load and are write-protected:", result.errored);
      }
      setLoading(false);
    }).catch(function (e) {
      // eslint-disable-next-line no-console
      console.error("loadAllData threw:", e);
      // Total load failure — nothing is saveable. User should retry.
      saveableKeys.current = new Set();
      setErroredKeys(["bills", "cards", "income", "payroll", "oneTimeTxs", "vaChanges", "salaryChanges", "startingBalance", "savingsBalance", "themeMode", "scenarios", "cardTargetPct"]);
      setLoading(false);
    });
  }

  function handleLogout() {
    setCurrentUser(null);
    setCurrentUserId(null);
    saveableKeys.current = new Set();
    setErroredKeys([]);
    // Reset to defaults
    setBills(INIT_BILLS);
    setCards(INIT_CARDS);
    setIncome(INIT_INCOME);
    setPayroll(INIT_PAYROLL);
    setOneTimeTxs([]);
    setVaChanges([]);
    setSalaryChanges([]);
    setProjDate("");
    setStartingBalance(0);
    setSavingsBalance(0);
    setScenarios([]);
    setActiveScenarioId(null);
    setCardTargetPct(28);
    setTab("bills");
  }

  // Auto-save helpers with per-key debounce.
  // A single shared timer is wrong: a quick sequence of saves to different keys
  // (e.g. delete a one-time tx that also restores a card balance) would clobber
  // earlier saves, since clearTimeout would discard them. Per-key timers ensure
  // every key persists independently.
  var saveTimers = useRef({});
  function autoSave(key, value) {
    if (loading || !currentUser) return;
    // Refuse to save keys that errored on load. Without this check, an edit
    // to (say) bills after a partial load failure would write our empty []
    // over whatever was actually in the database. The user must clear the
    // error condition first (typically via Force Refresh).
    if (!saveableKeys.current.has(key)) {
      // eslint-disable-next-line no-console
      console.warn("Save blocked for key '" + key + "' — not in saveable set. Use Force Refresh to retry load.");
      setSaveStatus("Save blocked");
      setTimeout(function () { setSaveStatus(""); }, 3000);
      return;
    }
    setSaveStatus("Saving...");
    if (saveTimers.current[key]) clearTimeout(saveTimers.current[key]);
    saveTimers.current[key] = setTimeout(function () {
      saveData(key, value).then(function (ok) {
        setSaveStatus(ok ? "Saved" : "Save failed");
        setTimeout(function () { setSaveStatus(""); }, 2000);
      });
    }, 800);
  }

  // makeUpdater: returns an updater that supports both value and updater-function args.
  // CRITICAL: when passed a function, autoSave must be called from INSIDE the
  // setState updater. The previous design read the computed value from a closure
  // variable AFTER calling setter(...), which only worked when React executed the
  // updater synchronously. In async contexts (promise callbacks, debounced timers)
  // React schedules the updater for later, so the closure variable was still
  // undefined when autoSave was called. The result: saveData(key, undefined),
  // which JSON.stringifies to undefined, encodes as empty bytes, encrypts empty,
  // and on next read produces empty plaintext + JSON.parse "Unexpected EOF".
  // That's the recurring bills/cards/payroll corruption pattern.
  function makeUpdater(setter, key) {
    return function (valOrFn) {
      if (typeof valOrFn === "function") {
        setter(function (prev) {
          var next = valOrFn(prev);
          autoSave(key, next);
          return next;
        });
      } else {
        setter(valOrFn);
        autoSave(key, valOrFn);
      }
    };
  }

  var updateBills = makeUpdater(setBills, "bills");
  var updateCards = makeUpdater(setCards, "cards");
  var updateIncome = makeUpdater(setIncome, "income");
  var updatePayroll = makeUpdater(setPayroll, "payroll");
  var updateOneTimeTxs = makeUpdater(setOneTimeTxs, "oneTimeTxs");
  var updateVaChanges = makeUpdater(setVaChanges, "vaChanges");
  var updateSalaryChanges = makeUpdater(setSalaryChanges, "salaryChanges");
  var updateStartingBalance = makeUpdater(setStartingBalance, "startingBalance");
  var updateSavingsBalance = makeUpdater(setSavingsBalance, "savingsBalance");
  var updateScenarios = makeUpdater(setScenarios, "scenarios");
  var updateCardTargetPct = makeUpdater(setCardTargetPct, "cardTargetPct");
  function updateThemeMode(val) { setThemeMode(val); autoSave("themeMode", val); }

  // Bundle all state into a single context value so child tabs don't need
  // long prop chains. Updaters use functional setState under the hood.
  // Note: this object is recreated on each render. Since only one tab is
  // mounted at a time and that tab needs to react to state changes anyway,
  // the cost is the same as what prop drilling produced before this refactor.
  var budgetCtx = {
    bills: bills, setBills: updateBills,
    cards: cards, setCards: updateCards,
    income: income, setIncome: updateIncome,
    payroll: payroll, setPayroll: updatePayroll,
    oneTimeTxs: oneTimeTxs, setOneTimeTxs: updateOneTimeTxs,
    vaChanges: vaChanges, setVaChanges: updateVaChanges,
    salaryChanges: salaryChanges, setSalaryChanges: updateSalaryChanges,
    startingBalance: startingBalance, setStartingBalance: updateStartingBalance,
    savingsBalance: savingsBalance, setSavingsBalance: updateSavingsBalance,
    scenarios: scenarios, setScenarios: updateScenarios,
    activeScenarioId: activeScenarioId, setActiveScenarioId: setActiveScenarioId,
    cardTargetPct: cardTargetPct, setCardTargetPct: updateCardTargetPct,
    currentUser: currentUser,
    themeMode: themeMode, setThemeMode: updateThemeMode,
    projDate: projDate, setProjDate: setProjDate,
  };

  if (!currentUser) return <LoginScreen onLogin={handleLogin} />;

  if (loading) {
    return (
      <div style={{ height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "linear-gradient(170deg, #060a14, #0e1525, #080d18)", fontFamily: "system-ui, sans-serif", color: T().textPrimary }}>
        <div style={{ fontSize: 22, fontWeight: 600, color: T().textSecondary, marginBottom: 8 }}>Vault Budget</div>
        <div style={{ fontSize: 13, color: T().textDim }}>Loading your data...</div>
      </div>
    );
  }

  return (
    <BudgetContext.Provider value={budgetCtx}>
    <div style={{ minHeight: "100vh", background: theme.bg, fontFamily: "system-ui, sans-serif", color: theme.textPrimary, position: "relative" }}>
      <div style={{ maxWidth: 960, margin: "0 auto", padding: "0 16px" }}>
      <div style={{ padding: "16px 0 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: theme.textSecondary }}>
            {tab === "bills" ? "Bills & Scenarios" : tab === "calendar" ? "Weekly Calendar" : tab === "debt" ? "Debt Payoff" : "Settings"}
          </h1>
          <p style={{ margin: "1px 0 0", fontSize: 11, color: theme.textDim }}>Vault Budget v{APP_VERSION}</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {saveStatus && <span style={{ fontSize: 10, color: saveStatus === "Saved" ? T().green : saveStatus === "Save failed" ? T().red : T().accent, fontFamily: "monospace" }}>{saveStatus}</span>}
          <span style={{ fontSize: 12, color: theme.textDim, fontFamily: "monospace" }}>{currentUser.username}</span>
          <button onClick={handleLogout} style={{ background: theme.lockBg, border: "1px solid " + theme.cardBd, borderRadius: 8, padding: "6px 14px", color: theme.accent, cursor: "pointer", fontSize: 12, fontFamily: "system-ui", fontWeight: 500 }}>Lock</button>
        </div>
      </div>

      {erroredKeys.length > 0 && (
        <div style={{ background: "rgba(196,92,92,0.12)", border: "1px solid " + T().red, borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: T().red, fontWeight: 700, marginBottom: 4 }}>Some data could not be loaded</div>
          <div style={{ fontSize: 11, color: T().textPrimary, lineHeight: 1.4 }}>
            The following sections failed to load and are write-protected to prevent overwriting your saved data: <span style={{ fontFamily: "monospace" }}>{erroredKeys.join(", ")}</span>. Use Settings → Force Refresh App to retry. Until then, edits to those sections will be blocked.
          </div>
        </div>
      )}

      {tab === "bills" && <ErrorBoundary label="Bills"><BillsTab /></ErrorBoundary>}
      {tab === "calendar" && <ErrorBoundary label="Calendar"><CalendarTab /></ErrorBoundary>}
      {tab === "debt" && <ErrorBoundary label="Debt"><DebtTab /></ErrorBoundary>}
      {tab === "settings" && <ErrorBoundary label="Settings"><SettingsTab /></ErrorBoundary>}
      </div>

      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: theme.navBg, borderTop: "1px solid " + theme.navBd, boxShadow: themeMode === "light" ? "0 -2px 12px rgba(0,0,0,0.08)" : "0 -2px 12px rgba(0,0,0,0.4)", display: "flex", justifyContent: "center", gap: 8, padding: "10px 0 calc(18px + env(safe-area-inset-bottom))", zIndex: 1000 }}>
        {[{ id: "bills", l: "Bills" }, { id: "calendar", l: "Calendar" }, { id: "debt", l: "Debt" }, { id: "settings", l: "Settings" }].map(function (t) {
          return <button key={t.id} onClick={function () { setTab(t.id); }} style={{ background: tab === t.id ? "rgba(" + (themeMode === "dark" ? "74,143,231" : "37,99,235") + ",0.12)" : "none", border: tab === t.id ? "1px solid " + theme.accent + "33" : "1px solid transparent", borderRadius: 8, cursor: "pointer", color: tab === t.id ? theme.accent : theme.textDim, fontFamily: "system-ui", padding: "8px 28px", fontSize: 14, fontWeight: tab === t.id ? 600 : 400, transition: "all 0.2s" }}>{t.l}</button>;
        })}
      </div>
    </div>
    </BudgetContext.Provider>
  );
}